import CorporateDocumentTypesModal from "../components/admin/CorporateDocumentTypesModal";
import { REGULATORY_ADD_ONS, regulatoryAddOns } from "../utils/useRegulatoryAddOns";
import { PEP_AI_PROVIDERS, pepAiProvider, pepAiProviderDescription } from "../utils/pepAiProvider";
import { normalizeAdverseLimits, validAdverseLimit, ADVERSE_LIMIT_BOUNDS } from "../utils/adverseMediaLimits";
import {
  DOCUMENT_REQUEST_TIMINGS,
  ENTITY_DOCUMENT_TYPES,
  entityDocTypes,
  documentsRequestConfig,
  documentsRequestSummary,
  intakeMaxPages,
  legacyDocumentsRequestMode,
} from "../utils/documentsRequest";
import { sanctionsSourcesForJurisdiction } from "../utils/sanctionsNameMatch";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";

import { motion } from "framer-motion";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";

import Select from "react-select";

import "./css/AdminUsersPage.css";
import { ROLES, ROLE_LABELS, roleLabel, isStaffCategory, normalizeRole } from "../auth/permissions";
import SearchBar from "../components/ui/SearchBar";
import { MAIL_API_BASE } from "../config/api";

// TESTING MODE - Call component used to delete all clients
import { appConfirm } from "../components/ui/appConfirm";
import { db } from "../firebase";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { purgeAllClientsAndKyc } from "../utils/purgeAllClientsAndKyc";
import { seedSampleClients } from "../utils/seedSampleClients50New";

const pageAnim = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
};

const TODO_CATEGORIES = [
  { key: "bug_fix", label: "Bug fixes" },
  { key: "enhancement", label: "Enhancements" },
  { key: "new_functionality", label: "New functionalities" },
];

const TEN_DAYS_MS = 10 * 24 * 60 * 60 * 1000;

const ROLE_OPTIONS = [
  { value: ROLES.STAFF_COMPLIANCE, label: ROLE_LABELS[ROLES.STAFF_COMPLIANCE] },
  { value: ROLES.STAFF_FRONT, label: ROLE_LABELS[ROLES.STAFF_FRONT] },
  { value: ROLES.MANAGER, label: ROLE_LABELS[ROLES.MANAGER] },
  { value: ROLES.ADMIN, label: ROLE_LABELS[ROLES.ADMIN] },
];

const ROLE_COMPARISON = [
  ["Main purpose", "Day-to-day KYC and compliance work", "Front-office KYC data entry", "Organisation-wide oversight", "Platform administration"],
  ["Client visibility", "Team clients, or own clients when not assigned to a team", "Team clients, or own clients when not assigned to a team", "All clients in their organisation", "Clients across organisations"],
  ["Create clients", "Yes, within assigned scope", "Yes, within assigned scope", "Yes, after choosing a team or individual owner", "Unavailable in the Clients screen"],
  ["Edit ordinary KYC information", "For accessible clients", "For accessible clients", "For clients in their organisation", "Across organisations"],
  ["Risk assessment, screenings, checklist, final overview", "Edit and decide", "Read-only", "Edit and decide", "Edit and decide"],
  ["Approve or reject documents (/kyc-inbox)", "Yes", "Read-only", "Yes", "Yes"],
  ["Delete clients", "Yes, within scope", "No", "Yes, in their organisation", "Yes"],
  ["Risk settings", "No", "No", "Their organisation’s settings", "All organisations and global defaults"],
  ["Manage users, roles and organisations", "No", "No", "No", "Yes, in Admin / Users"],
  ["Upload or refresh sanctions sources", "No", "No", "No", "Yes, in Admin / Sources"],
  ["Organisation membership required", "Yes", "Yes", "Yes", "No"],
];

const TEAM_ACCESS_OPTIONS = [
  { value: "none", label: "No team" },
  { value: "new", label: "New team" },
  { value: "existing", label: "Existing team" },
];

const USER_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "disabled", label: "Disabled" },
];

const ORG_TYPE_OPTIONS = ["A", "B", "C", "D", "E"];
const ORG_JURISDICTION_OPTIONS = [
  { value: "", label: "Not set" },
  { value: "mainland", label: "Mainland" },
  { value: "free_zone", label: "FZ" },
  { value: "difc", label: "DIFC" },
  { value: "adgm", label: "ADGM" },
];
const ORG_SUBSCRIPTION_OPTIONS = [
  { value: "essential", label: "Essential" },
  { value: "advanced", label: "Advanced" },
  { value: "scale", label: "Scale" },
  { value: "trial", label: "Trial" },
  { value: "beta", label: "Beta" },
];





const PROOF_OF_ADDRESS_DEFAULT_MAX_AGE_MONTHS = 3;
const PROOF_OF_ADDRESS_MAX_AGE_MONTHS = 36;
const DAYS_PER_MONTH_FOR_PROOF_OF_ADDRESS = 30;

// Links sent to clients: each valid 1 to 14 days (org settings, enforced by the API).
const KYC_LINK_VALIDITY_DAY_OPTIONS = Array.from({ length: 14 }, (_, i) => i + 1);
const CLIENT_LINK_VALIDITY_FIELDS = [
  { field: "kycLinkValidityDays", reminderField: "kycLinkReminderDays", secondReminderField: "kycLinkSecondReminderDays", settingKey: "kycFormLink", label: "KYC form" },
  { field: "documentUploadLinkValidityDays", reminderField: "documentUploadLinkReminderDays", secondReminderField: "documentUploadLinkSecondReminderDays", settingKey: "documentUploadLink", label: "Document upload" },
  { field: "documentsLaterLinkValidityDays", reminderField: "documentsLaterLinkReminderDays", secondReminderField: "documentsLaterLinkSecondReminderDays", settingKey: "documentsLaterLink", label: "Documents sent later" },
];

// Reminder: 0 (off) or 1 to validity - 1 days after sending (enforced by the API).
function kycLinkReminderDays(value, validityDays) {
  const days = Number(value);
  return Number.isInteger(days) && days >= 0 && days < validityDays ? days : 0;
}

// 2nd reminder: 0 (off) or 1 to 14 days after the link expired (it sends a new link).
function kycLinkSecondReminderDays(value) {
  const days = Number(value);
  return Number.isInteger(days) && days >= 0 && days <= 14 ? days : 0;
}

function kycLinkValidityText(value) {
  const days = kycLinkValidityDays(value);
  return days === 1 ? "1 day (24 hours)" : `${days} days`;
}

function kycLinkValidityDays(value) {
  const days = Number(value);
  return Number.isInteger(days) && days >= 1 && days <= 14 ? days : 1;
}

function proofOfAddressDaysToMonths(value) {
  const days = Number(value);
  if (!Number.isFinite(days) || days <= 0) return PROOF_OF_ADDRESS_DEFAULT_MAX_AGE_MONTHS;
  return Math.min(
    PROOF_OF_ADDRESS_MAX_AGE_MONTHS,
    Math.max(1, Math.round(days / DAYS_PER_MONTH_FOR_PROOF_OF_ADDRESS))
  );
}

function proofOfAddressMonthsToDays(value) {
  const months = Number(value);
  if (!Number.isFinite(months) || months <= 0) {
    return PROOF_OF_ADDRESS_DEFAULT_MAX_AGE_MONTHS * DAYS_PER_MONTH_FOR_PROOF_OF_ADDRESS;
  }
  return (
    Math.min(PROOF_OF_ADDRESS_MAX_AGE_MONTHS, Math.max(1, Math.round(months))) *
    DAYS_PER_MONTH_FOR_PROOF_OF_ADDRESS
  );
}

function orgSubscriptionLabel(value) {
  const clean = String(value || "").trim();
  const option = ORG_SUBSCRIPTION_OPTIONS.find((item) => item.value === clean);
  if (option) return option.label;
  return clean ? titleCaseWords(clean.replace(/[_-]+/g, " ")) : "";
}

function orgJurisdictionLabel(value) {
  const clean = String(value || "").trim();
  const option = ORG_JURISDICTION_OPTIONS.find((item) => item.value === clean);
  return option?.label || "";
}


function titleCaseWords(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/(^|[\s'-])([a-z])/g, (match, separator, letter) =>
      `${separator}${letter.toUpperCase()}`
    );
}

function currentMonthKey() {
  return new Date().toISOString().slice(0, 7);
}

function monthKeyFromValue(value) {
  const rawDate =
    typeof value?.toDate === "function"
      ? value.toDate()
      : value instanceof Date
      ? value
      : value
      ? new Date(value)
      : null;
  if (rawDate && !Number.isNaN(rawDate.getTime())) {
    return rawDate.toISOString().slice(0, 7);
  }
  return "";
}

function usageMonthFromDoc(data) {
  const explicit = String(data?.monthKey || "").trim();
  if (/^\d{4}-\d{2}$/.test(explicit)) return explicit;
  return monthKeyFromValue(data?.createdAt) || monthKeyFromValue(data?.createdAtIso);
}

function documentMonthFromSubmission(data, file) {
  return (
    monthKeyFromValue(file?.uploadedAt) ||
    monthKeyFromValue(file?.createdAt) ||
    monthKeyFromValue(data?.submittedAt) ||
    monthKeyFromValue(data?.updatedAt) ||
    monthKeyFromValue(data?.createdAt)
  );
}

function scanMonthFromSubmission(data, file, scanResult) {
  return (
    monthKeyFromValue(scanResult?.scannedAt) ||
    monthKeyFromValue(scanResult?.createdAt) ||
    monthKeyFromValue(scanResult?.updatedAt) ||
    documentMonthFromSubmission(data, file)
  );
}

function scanEstimatedCostUsd(scanResult) {
  return (
    Number(scanResult?.estimatedTotalCostUsd) ||
    Number(scanResult?.estimatedCostUsd) ||
    Number(scanResult?.costUsd) ||
    Number(scanResult?.usage?.estimatedTotalCostUsd) ||
    Number(scanResult?.usage?.estimatedCostUsd) ||
    0
  );
}

function completedDocumentScanResults(file) {
  return [
    { key: "id", result: file?.documentDataConsistencyCheck },
    { key: "utility", result: file?.proofOfAddressCheck },
    { key: "corporate", result: file?.corporateDocumentCheck },
  ].filter(({ result }) => {
    const status = String(result?.status || result?.overallStatus || "").trim().toLowerCase();
    return result && status !== "pending";
  });
}

function normalizeUsageType(value) {
  const type = String(value || "").trim().toLowerCase();
  if (type === "adverse" || type === "adverse-media") return "adverse_media";
  if (type === "sanction") return "sanctions";
  return type;
}

function formatUsd(value) {
  const n = Number(value) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: n > 0 && n < 0.01 ? 4 : 2,
  }).format(n);
}

function screeningUsageMetric(count, cost) {
  return (
    <div className="screeningUsageMetric">
      <div>{count}</div>
      <div className="screeningUsageMetricCost">{formatUsd(cost)}</div>
    </div>
  );
}

function formatStorageMb(bytes) {
  const mb = (Number(bytes) || 0) / (1024 * 1024);
  return `${mb.toFixed(2)} MB`;
}

function documentStorageMetric(count, bytes) {
  return (
    <div className="screeningUsageMetric">
      <div>{count}</div>
      <div className="screeningUsageMetricCost">{formatStorageMb(bytes)}</div>
    </div>
  );
}

const API_BASE = MAIL_API_BASE;

async function apiFetch(path, { method = "GET", token, body } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const json = await res.json().catch(() => ({}));

  if (!res.ok || json?.ok === false) {
    const msg = json?.error || json?.message || "Request failed";
    const err = new Error(msg);
    err.status = res.status;
    err.code = json?.code;
    err.impact = json?.impact;
    throw err;
  }

  return json;
}

{
  /* ********************************************************************* */
}
{
  /* ************************ EXPORT FUNCTION ************************** */
}
{
  /* ********************************************************************* */
}

export default function AdminUsersPage() {
  const nav = useNavigate();
  const toastRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [users, setUsers] = useState([]);

  // ✅ Organisations
  const [orgs, setOrgs] = useState([]);
  const [orgLoading, setOrgLoading] = useState(true);
  const [orgErr, setOrgErr] = useState("");
  const [orgSearch, setOrgSearch] = useState("");
  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgGeo, setNewOrgGeo] = useState("uae");
  const [newOrgJurisdiction, setNewOrgJurisdiction] = useState("");
  const [newOrgIndustry, setNewOrgIndustry] = useState("finance");
  const [newOrgType, setNewOrgType] = useState("A");
  const [newOrgSubscription, setNewOrgSubscription] = useState("trial");
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showRoleInfo, setShowRoleInfo] = useState(false);
  const [editOrgModal, setEditOrgModal] = useState(null); // { orgId, name, geo, industry, type, subscription }
  const [orgSettingsModal, setOrgSettingsModal] = useState(null);
  const [docTypesInfoOpen, setDocTypesInfoOpen] = useState(false);
  const [savingOrgSettings, setSavingOrgSettings] = useState(false);

  // ✅ Create user form
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(ROLES.STAFF_FRONT);
  const [orgId, setOrgId] = useState(""); // ✅ now starts empty
  const [teamMode, setTeamMode] = useState("none");
  const [teamName, setTeamName] = useState("");
  const [selectedTeamOption, setSelectedTeamOption] = useState(null);
  const [teamAccessModal, setTeamAccessModal] = useState(null);
  const [pendingTeamName, setPendingTeamName] = useState("");
  const [pendingTeamOption, setPendingTeamOption] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editUserModal, setEditUserModal] = useState(null);
  const [savingUserEdit, setSavingUserEdit] = useState(false);
  const [reassignModal, setReassignModal] = useState(null);
  const [reassignTeamMode, setReassignTeamMode] = useState("none");
  const [reassignTeamName, setReassignTeamName] = useState("");
  const [reassignTeamOption, setReassignTeamOption] = useState(null);
  const [reassignMoveMode, setReassignMoveMode] = useState("staff_only");
  const [reassignSelectedClientIds, setReassignSelectedClientIds] = useState([]);
  const [savingReassign, setSavingReassign] = useState(false);
  const [deleteClientReassignModal, setDeleteClientReassignModal] = useState(null);
  const [deleteClientTargetMode, setDeleteClientTargetMode] = useState("staff");
  const [deleteClientTargetStaff, setDeleteClientTargetStaff] = useState(null);
  const [deleteClientTargetTeam, setDeleteClientTargetTeam] = useState(null);
  const [resolvingDeleteClients, setResolvingDeleteClients] = useState(false);

  // ✅ react-select selected option
  const [selectedOrgOption, setSelectedOrgOption] = useState(null);

  // ✅ highlighted org row
  const [highlightedOrgId, setHighlightedOrgId] = useState(null);

  // ✅ List filters
  const [search, setSearch] = useState("");

  // ✅ Empty-users deletion progress modal
  const [deletionProgress, setDeletionProgress] = useState(null);

  // ✅ TODO modal
  const [showTodoModal, setShowTodoModal] = useState(false);
  const [todos, setTodos] = useState([]);
  const [todoLoading, setTodoLoading] = useState(false);
  const [todoInputs, setTodoInputs] = useState({ bug_fix: "", enhancement: "", new_functionality: "" });
  const [todoPriority, setTodoPriority] = useState({ bug_fix: "normal", enhancement: "normal", new_functionality: "normal" });
  const [todoSaving, setTodoSaving] = useState({ bug_fix: false, enhancement: false, new_functionality: false });

  // ✅ Client counts per org
  const [clientCountByOrgId, setClientCountByOrgId] = useState({});
  const [purgingOrgClientsId, setPurgingOrgClientsId] = useState("");

  // ✅ Screening usage per org/month
  const [screeningUsageRows, setScreeningUsageRows] = useState([]);
  const [documentStorageRows, setDocumentStorageRows] = useState([]);
  const [documentScanRows, setDocumentScanRows] = useState([]);
  const [screeningUsageLoading, setScreeningUsageLoading] = useState(true);
  const [screeningUsageErr, setScreeningUsageErr] = useState("");
  const [resettingScreeningUsageOrgId, setResettingScreeningUsageOrgId] =
    useState("");

  async function getTokenOrThrow() {
    const u = auth.currentUser;
    if (!u) throw new Error("You must be signed in.");
    return u.getIdToken(true);
  }

  async function loadUsers() {
    setErr("");
    setLoading(true);

    try {
      const token = await getTokenOrThrow();
      const json = await apiFetch("/myKyc/admin/users", { token });
      setUsers(Array.isArray(json.users) ? json.users : []);
    } catch (e) {
      setErr(e?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }

  async function loadOrgs() {
    setOrgErr("");
    setOrgLoading(true);

    try {
      const token = await getTokenOrThrow();
      const json = await apiFetch("/myKyc/admin/orgs", { token });
      setOrgs(Array.isArray(json.orgs) ? json.orgs : []);
    } catch (e) {
      setOrgErr(e?.message || "Failed to load organisations");
    } finally {
      setOrgLoading(false);
    }
  }

  async function loadClientCounts(orgList) {
    if (!orgList.length) { setClientCountByOrgId({}); return; }
    const entries = await Promise.all(
      orgList.map(async (o) => {
        try {
          const q = query(collection(db, "clients"), where("orgId", "==", o.orgId));
          const snap = await getCountFromServer(q);
          return [o.orgId, snap.data().count];
        } catch {
          return [o.orgId, null];
        }
      })
    );
    setClientCountByOrgId(Object.fromEntries(entries));
  }

  async function loadScreeningUsage() {
    setScreeningUsageErr("");
    setScreeningUsageLoading(true);

    try {
      const [usageSnap, submissionsSnap] = await Promise.all([
        getDocs(collection(db, "screeningUsage")),
        getDocs(collection(db, "documentSubmissions")),
      ]);

      const rows = usageSnap.docs.map((docSnap) => {
        const data = docSnap.data() || {};
        return {
          id: docSnap.id,
          orgId: String(data.orgId || "").trim(),
          screeningType: normalizeUsageType(data.screeningType),
          namesCount: Number(data.namesCount) || 0,
          estimatedTotalCostUsd: Number(data.estimatedTotalCostUsd) || 0,
          monthKey: usageMonthFromDoc(data),
        };
      });

      const seenStorageObjects = new Set();
      const storageRows = [];
      const scanRows = [];
      for (const docSnap of submissionsSnap.docs) {
        const data = docSnap.data() || {};
        const orgId = String(data.orgId || "").trim();
        const files = Array.isArray(data.files) ? data.files : [];
        if (!orgId || !files.length) continue;

        files.forEach((file, index) => {
          const objectPath = String(file?.objectPath || "").trim();
          const dedupeKey = objectPath || `${docSnap.id}:${index}`;
          if (seenStorageObjects.has(dedupeKey)) return;
          seenStorageObjects.add(dedupeKey);

          storageRows.push({
            id: dedupeKey,
            orgId,
            bytes: Number(file?.sizeBytes ?? file?.size ?? 0) || 0,
            monthKey: documentMonthFromSubmission(data, file),
          });

          completedDocumentScanResults(file).forEach(({ key: scanKind, result }) => {
            scanRows.push({
              id: `${dedupeKey}:${scanKind}`,
              orgId,
              monthKey: scanMonthFromSubmission(data, file, result),
              estimatedTotalCostUsd: scanEstimatedCostUsd(result),
            });
          });
        });
      }

      setScreeningUsageRows(rows);
      setDocumentStorageRows(storageRows);
      setDocumentScanRows(scanRows);
    } catch (e) {
      setScreeningUsageErr(e?.message || "Failed to load API usage");
      setScreeningUsageRows([]);
      setDocumentStorageRows([]);
      setDocumentScanRows([]);
    } finally {
      setScreeningUsageLoading(false);
    }
  }

  function handleResetScreeningUsage(row) {
    const orgId = String(row?.orgId || "").trim();
    if (!orgId || resettingScreeningUsageOrgId) return;

    const orgName = String(row?.orgName || orgId).trim();

    appConfirm({
      group: "appConfirm",
      header: "Reset API usage?",
      message: `Reset all API usage records for "${orgName}"?\n\nThis will delete the usage records behind this row and reset counts/costs to zero.`,
      scheme: "danger",
      icon: "bi bi-exclamation-triangle",
      acceptLabel: "Reset usage",
      rejectLabel: "Cancel",
      accept: async () => {
        setResettingScreeningUsageOrgId(orgId);
        try {
          const q = query(collection(db, "screeningUsage"), where("orgId", "==", orgId));
          const snap = await getDocs(q);
          await Promise.all(
            snap.docs.map((docSnap) =>
              deleteDoc(doc(db, "screeningUsage", docSnap.id))
            )
          );
          setScreeningUsageRows((prev) =>
            (prev || []).filter((usageRow) => usageRow.orgId !== orgId)
          );
          toastRef.current?.show({
            severity: "success",
            summary: "Usage reset",
            detail: `Deleted ${snap.size} usage record(s) for ${orgName}.`,
            life: 3500,
          });
        } catch (e) {
          toastRef.current?.show({
            severity: "error",
            summary: "Reset failed",
            detail: e?.message || "Failed to reset API usage",
            life: 4500,
          });
        } finally {
          setResettingScreeningUsageOrgId("");
        }
      },
    });
  }

  useEffect(() => {
    // load both in parallel
    (async () => {
      await Promise.allSettled([loadUsers(), loadOrgs()]);
      await loadScreeningUsage();
    })();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (orgs.length > 0) loadClientCounts(orgs);
    // eslint-disable-next-line
  }, [orgs]);

  // Count staff users per org (based on /myKyc/admin/users result)
  const userCountByOrgId = useMemo(() => {
    const map = {};
    for (const u of users || []) {
      const oid = String(u.orgId || "").trim();
      if (!oid) continue;
      map[oid] = (map[oid] || 0) + 1;
    }
    return map;
  }, [users]);

  // Count staff users disabled per org
  const disabledUserCountByOrgId = useMemo(() => {
    const map = {};
    for (const u of users || []) {
      const oid = String(u.orgId || "").trim();
      if (!oid) continue;
      if (u.disabled === true) {
        map[oid] = (map[oid] || 0) + 1;
      }
    }
    return map;
  }, [users]);

  const screeningUsageByOrg = useMemo(() => {
    const orgById = new Map((orgs || []).map((o) => [String(o.orgId || ""), o]));
    const current = currentMonthKey();
    const map = new Map();

    const ensure = (orgId) => {
      const key = orgId;
      if (!map.has(key)) {
        const org = orgById.get(orgId) || {};
        map.set(key, {
          key,
          orgId,
          orgName: org.name || orgId || "—",
          pep: 0,
          pepMonth: 0,
          sanctions: 0,
          sanctionsMonth: 0,
          adverseMedia: 0,
          adverseMediaMonth: 0,
          other: 0,
          otherMonth: 0,
          storageDocs: 0,
          storageDocsMonth: 0,
          storageBytes: 0,
          storageBytesMonth: 0,
          pepCostUsd: 0,
          pepCostMonthUsd: 0,
          sanctionsCostUsd: 0,
          sanctionsCostMonthUsd: 0,
          adverseMediaCostUsd: 0,
          adverseMediaCostMonthUsd: 0,
          otherCostUsd: 0,
          otherCostMonthUsd: 0,
          costUsd: 0,
          costMonthUsd: 0,
        });
      }
      return map.get(key);
    };

    for (const o of orgs || []) {
      const oid = String(o.orgId || "").trim();
      if (oid) ensure(oid);
    }

    for (const row of screeningUsageRows || []) {
      const orgId = String(row.orgId || "").trim();
      const monthKey = String(row.monthKey || "").trim();
      const count = Number(row.namesCount) || 0;
      const estimatedCost = Number(row.estimatedTotalCostUsd) || 0;
      if (!orgId) continue;

      const entry = ensure(orgId);
      const type = normalizeUsageType(row.screeningType);
      const isCurrentMonth = monthKey === current;
      entry.costUsd += estimatedCost;
      if (isCurrentMonth) entry.costMonthUsd += estimatedCost;
      if (type === "pep") {
        if (count > 0) entry.pep += count;
        entry.pepCostUsd += estimatedCost;
        if (isCurrentMonth && count > 0) entry.pepMonth += count;
        if (isCurrentMonth) entry.pepCostMonthUsd += estimatedCost;
      }
      if (type === "sanctions") {
        if (count > 0) entry.sanctions += count;
        entry.sanctionsCostUsd += estimatedCost;
        if (isCurrentMonth && count > 0) entry.sanctionsMonth += count;
        if (isCurrentMonth) entry.sanctionsCostMonthUsd += estimatedCost;
      }
      if (type === "adverse_media") {
        if (count > 0) entry.adverseMedia += count;
        entry.adverseMediaCostUsd += estimatedCost;
        if (isCurrentMonth && count > 0) entry.adverseMediaMonth += count;
        if (isCurrentMonth) entry.adverseMediaCostMonthUsd += estimatedCost;
      }
      if (type !== "pep" && type !== "sanctions" && type !== "adverse_media") {
        const otherCount = count > 0 ? count : estimatedCost > 0 ? 1 : 0;
        entry.other += otherCount;
        entry.otherCostUsd += estimatedCost;
        if (isCurrentMonth) {
          entry.otherMonth += otherCount;
          entry.otherCostMonthUsd += estimatedCost;
        }
      }
    }

    for (const row of documentStorageRows || []) {
      const orgId = String(row.orgId || "").trim();
      const monthKey = String(row.monthKey || "").trim();
      const bytes = Number(row.bytes) || 0;
      if (!orgId) continue;

      const entry = ensure(orgId);
      const isCurrentMonth = monthKey === current;
      entry.storageDocs += 1;
      entry.storageBytes += bytes;
      if (isCurrentMonth) {
        entry.storageDocsMonth += 1;
        entry.storageBytesMonth += bytes;
      }
    }

    for (const row of documentScanRows || []) {
      const orgId = String(row.orgId || "").trim();
      const monthKey = String(row.monthKey || "").trim();
      const estimatedCost = Number(row.estimatedTotalCostUsd) || 0;
      if (!orgId) continue;

      const entry = ensure(orgId);
      const isCurrentMonth = monthKey === current;
      entry.other += 1;
      entry.otherCostUsd += estimatedCost;
      entry.costUsd += estimatedCost;
      if (isCurrentMonth) {
        entry.otherMonth += 1;
        entry.otherCostMonthUsd += estimatedCost;
        entry.costMonthUsd += estimatedCost;
      }
    }

    return Array.from(map.values())
      .map((row) => ({
        ...row,
        total: row.pep + row.sanctions + row.adverseMedia,
        totalMonth: row.pepMonth + row.sanctionsMonth + row.adverseMediaMonth,
        costUsd: row.costUsd,
        costMonthUsd: row.costMonthUsd,
      }))
      .sort((a, b) => String(a.orgName).localeCompare(String(b.orgName)));
  }, [orgs, screeningUsageRows, documentStorageRows, documentScanRows]);

  const filteredScreeningUsageByOrg = useMemo(() => {
    const q = orgSearch.trim().toLowerCase();
    if (!q) return screeningUsageByOrg;

    return (screeningUsageByOrg || []).filter((row) => {
      const id = String(row.orgId || "").toLowerCase();
      const name = String(row.orgName || "").toLowerCase();
      return id.includes(q) || name.includes(q);
    });
  }, [screeningUsageByOrg, orgSearch]);

  const screeningUsageTotals = useMemo(() => {
    return filteredScreeningUsageByOrg.reduce(
      (acc, row) => {
        acc.pep += row.pep;
        acc.pepMonth += row.pepMonth;
        acc.sanctions += row.sanctions;
        acc.sanctionsMonth += row.sanctionsMonth;
        acc.adverseMedia += row.adverseMedia;
        acc.adverseMediaMonth += row.adverseMediaMonth;
        acc.other += row.other;
        acc.otherMonth += row.otherMonth;
        acc.storageDocs += row.storageDocs;
        acc.storageDocsMonth += row.storageDocsMonth;
        acc.storageBytes += row.storageBytes;
        acc.storageBytesMonth += row.storageBytesMonth;
        acc.pepCostUsd += row.pepCostUsd;
        acc.pepCostMonthUsd += row.pepCostMonthUsd;
        acc.sanctionsCostUsd += row.sanctionsCostUsd;
        acc.sanctionsCostMonthUsd += row.sanctionsCostMonthUsd;
        acc.adverseMediaCostUsd += row.adverseMediaCostUsd;
        acc.adverseMediaCostMonthUsd += row.adverseMediaCostMonthUsd;
        acc.otherCostUsd += row.otherCostUsd;
        acc.otherCostMonthUsd += row.otherCostMonthUsd;
        acc.total += row.total;
        acc.totalMonth += row.totalMonth;
        acc.costUsd += row.costUsd;
        acc.costMonthUsd += row.costMonthUsd;
        return acc;
      },
      {
        pep: 0,
        pepMonth: 0,
        sanctions: 0,
        sanctionsMonth: 0,
        adverseMedia: 0,
        adverseMediaMonth: 0,
        other: 0,
        otherMonth: 0,
        storageDocs: 0,
        storageDocsMonth: 0,
        storageBytes: 0,
        storageBytesMonth: 0,
        pepCostUsd: 0,
        pepCostMonthUsd: 0,
        sanctionsCostUsd: 0,
        sanctionsCostMonthUsd: 0,
        adverseMediaCostUsd: 0,
        adverseMediaCostMonthUsd: 0,
        otherCostUsd: 0,
        otherCostMonthUsd: 0,
        total: 0,
        totalMonth: 0,
        costUsd: 0,
        costMonthUsd: 0,
      }
    );
  }, [filteredScreeningUsageByOrg]);

  // Logout
  async function handleLogout() {
    sessionStorage.setItem("mykyc:loggedOut", "1");
    await signOut(auth);
    nav("/login", { replace: true });
  }

  // Create user
  async function handleCreateUser(e) {
    e.preventDefault();
    if (creating) return;

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) return setErr("Email is required.");
    if (!cleanEmail.includes("@")) return setErr("Invalid email.");
    if (!fullName.trim()) return setErr("Full name is required.");
    if (!String(orgId || "").trim())
      return setErr(
        "Org ID is required (create/select an organisation first)."
      );

    // Prevent assigning disabled org
    const org = orgById[String(orgId || "").trim()];
    if (org?.disabled) {
      return setErr(
        "This organisation is disabled. Please select another one."
      );
    }

    const isStaffRole = isStaffCategory(role);
    if (isStaffRole && teamMode === "existing" && !selectedTeamOption?.value) {
      return setErr("Please select an existing team for this staff member.");
    }
    if (isStaffRole && teamMode === "new" && !String(teamName || "").trim()) {
      return setErr("Please enter a team name or keep No team selected.");
    }

    setErr("");
    setCreating(true);

    try {
      const token = await getTokenOrThrow();

      const payload = {
        email: cleanEmail,
        fullName: fullName.trim(),
        role,
        orgId: String(orgId).trim(),
        teamMode: isStaffRole ? teamMode : "",
        teamId: isStaffRole && teamMode === "existing" ? selectedTeamOption.value : "",
        teamName:
          isStaffRole && teamMode === "existing"
            ? selectedTeamOption.teamName || selectedTeamOption.label
            : isStaffRole && teamMode === "new"
            ? String(teamName || "").trim()
            : "",
      };

      await apiFetch("/myKyc/admin/create-user", {
        method: "POST",
        token,
        body: payload,
      });

      toastRef.current?.show({
        severity: "success",
        summary: "User created",
        detail: `Activation link sent to ${cleanEmail}`,
        life: 3500,
      });

      setFullName("");
      setEmail("");
      setRole(ROLES.STAFF_FRONT);
      setOrgId("");
      setSelectedOrgOption(null);
      setTeamMode("none");
      setTeamName("");
      setSelectedTeamOption(null);

      await loadUsers();
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Create failed",
        detail: e?.message || "Failed to create user",
        life: 4500,
      });
    } finally {
      setCreating(false);
    }
  }

  // Create organisation
  async function handleCreateOrg(e) {
    e.preventDefault();
    if (creatingOrg) return;

    const name = newOrgName.trim();
    if (!name) return setOrgErr("Organisation name is required.");

    setOrgErr("");
    setCreatingOrg(true);

    try {
      const token = await getTokenOrThrow();

      const json = await apiFetch("/myKyc/admin/create-org", {
        method: "POST",
        token,
        body: {
          orgName: name,
          geo: newOrgGeo,
          jurisdiction: newOrgGeo === "uae" ? newOrgJurisdiction : "",
          industry: newOrgIndustry,
          type: newOrgType,
          subscription: newOrgSubscription,
        },
      });

      toastRef.current?.show({
        severity: "success",
        summary: "Organisation created",
        detail: `${json?.name || name}`,
        life: 3000,
      });

      setNewOrgName("");
      setNewOrgGeo("uae");
      setNewOrgJurisdiction("");
      setNewOrgIndustry("finance");
      setNewOrgType("A");
      setNewOrgSubscription("trial");
      setShowCreateOrgModal(false);

      await loadOrgs();

      // ✅ auto-select it for user creation
      if (json?.orgId) {
        setOrgId(json.orgId);
        setSelectedOrgOption({
          value: json.orgId,
          label: json?.name || name,
        });
      }
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Org creation failed",
        detail: e?.message || "Failed to create organisation",
        life: 4500,
      });
    } finally {
      setCreatingOrg(false);
    }
  }

  async function handleSaveOrgMeta() {
    if (!editOrgModal) return;
    try {
      const token = await getTokenOrThrow();
      await apiFetch("/myKyc/admin/update-org", {
        method: "POST",
        token,
        body: {
          orgId: editOrgModal.orgId,
          geo: editOrgModal.geo,
          jurisdiction: editOrgModal.geo === "uae" ? editOrgModal.jurisdiction : "",
          industry: editOrgModal.industry,
          type: editOrgModal.type,
          subscription: editOrgModal.subscription,
        },
      });
      toastRef.current?.show({
        severity: "success",
        summary: "Organisation updated",
        detail: `${editOrgModal.name} saved.`,
        life: 2500,
      });
      setEditOrgModal(null);
      await loadOrgs();
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Update failed",
        detail: e?.message || "Failed to update organisation",
        life: 4500,
      });
    }
  }

  function openEditOrgModal(org) {
    setEditOrgModal({
      orgId: org.orgId,
      name: org.name,
      geo: org.geo || "uae",
      jurisdiction: org.jurisdiction || "",
      industry: org.industry || "finance",
      type: org.type || "A",
      subscription: org.subscription || "trial",
    });
  }

  function openOrgSettingsModal(org) {
    setOrgSettingsModal({
      orgId: org.orgId,
      name: org.name || org.orgId,
      nameEntryMode: org.nameEntryMode === "separate" ? "separate" : "full",
      nameEntryLocked: org.nameEntryLocked === true || clientCountByOrgId[org.orgId] !== 0,
      pepAiProvider: pepAiProvider(org?.settings?.pepScreening?.aiProvider),
      regulatoryAddOns: regulatoryAddOns(org?.settings?.regulatoryAddOns),
      enhancedAdverseScreening: org?.settings?.adverseMediaScreening?.enhancedEnabled === true,
      capAdverseAllegations: org?.settings?.adverseMediaScreening?.capAllegationsAtMedium === true,
      adverseResultsPerQuery: normalizeAdverseLimits(org?.settings?.adverseMediaScreening).resultsPerQuery,
      adverseArticlesPerSubject: normalizeAdverseLimits(org?.settings?.adverseMediaScreening).articlesPerSubject,
      documentsRequest: documentsRequestConfig(org?.settings),
      complianceAssessmentEnabled:
        org?.settings?.complianceAssessment?.enabled !== false,
      documentDataConsistencyScanIdEnabled:
        org?.settings?.documentDataConsistencyCheck?.scanIdEnabled === true,
      proofOfAddressVerificationEnabled:
        org?.settings?.proofOfAddressVerification?.enabled === true,
      proofOfAddressValidityUnlimited:
        org?.settings?.proofOfAddressVerification?.validityUnlimited === true ||
        org?.settings?.proofOfAddressVerification?.maxAgeDays === null,
      proofOfAddressMaxAgeMonths:
        proofOfAddressDaysToMonths(
          org?.settings?.proofOfAddressVerification?.maxAgeDays
        ),
      proofOfAddressIssuerCheckEnabled:
        org?.settings?.proofOfAddressVerification?.issuerCheckEnabled !== false,
      corporateDocumentVerificationEnabled:
        org?.settings?.corporateDocumentVerification?.enabled === true,
      corporateDocumentValidityUnlimited:
        org?.settings?.corporateDocumentVerification?.validityUnlimited === true ||
        org?.settings?.corporateDocumentVerification?.maxAgeDays === null,
      corporateDocumentMaxAgeMonths:
        proofOfAddressDaysToMonths(
          org?.settings?.corporateDocumentVerification?.maxAgeDays
        ),
      sanctionsScreeningSources: sanctionsSourcesForJurisdiction(org?.geo),
      sanctionsJurisdiction: org?.geo || "",
      ...Object.fromEntries(CLIENT_LINK_VALIDITY_FIELDS.flatMap(({ field, reminderField, secondReminderField, settingKey }) => {
        const validityDays = kycLinkValidityDays(org?.settings?.[settingKey]?.validityDays);
        return [
          [field, validityDays],
          [reminderField, kycLinkReminderDays(org?.settings?.[settingKey]?.reminderDays, validityDays)],
          [secondReminderField, kycLinkSecondReminderDays(org?.settings?.[settingKey]?.secondReminderDays)],
        ];
      })),
    });
  }

  async function handleSaveOrgSettings() {
    if (!orgSettingsModal || savingOrgSettings) return;

    try {
      setSavingOrgSettings(true);
      if (!validAdverseLimit('resultsPerQuery', orgSettingsModal.adverseResultsPerQuery)
        || !validAdverseLimit('articlesPerSubject', orgSettingsModal.adverseArticlesPerSubject)) {
        throw new Error('Adverse screening requires whole numbers: 1–10 results per query and 1–50 articles per subject.');
      }
      const nextSettings = {
        regulatoryAddOns: regulatoryAddOns(orgSettingsModal.regulatoryAddOns),
        adverseMediaScreening: {
          enhancedEnabled: orgSettingsModal.enhancedAdverseScreening === true,
          capAllegationsAtMedium: orgSettingsModal.capAdverseAllegations === true,
          resultsPerQuery: Number(orgSettingsModal.adverseResultsPerQuery),
          articlesPerSubject: Number(orgSettingsModal.adverseArticlesPerSubject),
        },
        pepScreening: {
          // The screening API reads this organisation setting for each new search.
          aiProvider: pepAiProvider(orgSettingsModal.pepAiProvider),
        },
        // Normalised: every field defined (Firestore rejects undefined), CV only with "start".
        documentsRequest: {
          ...documentsRequestConfig({ documentsRequest: orgSettingsModal.documentsRequest }),
          mode: legacyDocumentsRequestMode(documentsRequestConfig({ documentsRequest: orgSettingsModal.documentsRequest })),
        },
        complianceAssessment: {
          enabled: orgSettingsModal.complianceAssessmentEnabled === true,
        },
        documentDataConsistencyCheck: {
          scanIdEnabled:
            orgSettingsModal.documentDataConsistencyScanIdEnabled === true,
        },
        proofOfAddressVerification: {
          enabled:
            orgSettingsModal.proofOfAddressVerificationEnabled === true,
          validityUnlimited:
            orgSettingsModal.proofOfAddressValidityUnlimited === true,
          maxAgeDays:
            orgSettingsModal.proofOfAddressValidityUnlimited === true
              ? null
              : proofOfAddressMonthsToDays(
                  orgSettingsModal.proofOfAddressMaxAgeMonths
                ),
          issuerCheckEnabled:
            orgSettingsModal.proofOfAddressIssuerCheckEnabled !== false,
        },
        corporateDocumentVerification: {
          enabled:
            orgSettingsModal.corporateDocumentVerificationEnabled === true,
          validityUnlimited:
            orgSettingsModal.corporateDocumentValidityUnlimited === true,
          maxAgeDays:
            orgSettingsModal.corporateDocumentValidityUnlimited === true
              ? null
              : proofOfAddressMonthsToDays(
                  orgSettingsModal.corporateDocumentMaxAgeMonths
                ),
        },
        sanctionsScreening: {
          sources: sanctionsSourcesForJurisdiction(orgSettingsModal.sanctionsJurisdiction),
        },
      };
      const token = await getTokenOrThrow();
      const settingsResult = await apiFetch("/myKyc/admin/update-org-settings", {
        method: "POST",
        token,
        body: {
          orgId: orgSettingsModal.orgId,
          nameEntryMode: orgSettingsModal.nameEntryMode,
          documentsRequest: {
            individuals: nextSettings.documentsRequest.individuals,
            individualsCv: nextSettings.documentsRequest.individualsCv,
            entities: nextSettings.documentsRequest.entities,
            entitiesDocTypes: nextSettings.documentsRequest.entitiesDocTypes,
            maxPages: nextSettings.documentsRequest.maxPages,
          },
          documentsRequestMode: nextSettings.documentsRequest.mode,
          complianceAssessmentEnabled: nextSettings.complianceAssessment.enabled,
          documentDataConsistencyScanIdEnabled:
            nextSettings.documentDataConsistencyCheck.scanIdEnabled,
          proofOfAddressVerificationEnabled:
            nextSettings.proofOfAddressVerification.enabled,
          proofOfAddressValidityUnlimited:
            nextSettings.proofOfAddressVerification.validityUnlimited,
          proofOfAddressMaxAgeDays:
            nextSettings.proofOfAddressVerification.maxAgeDays,
          proofOfAddressIssuerCheckEnabled:
            nextSettings.proofOfAddressVerification.issuerCheckEnabled,
          corporateDocumentVerificationEnabled:
            nextSettings.corporateDocumentVerification.enabled,
          corporateDocumentValidityUnlimited:
            nextSettings.corporateDocumentVerification.validityUnlimited,
          corporateDocumentMaxAgeDays:
            nextSettings.corporateDocumentVerification.maxAgeDays,
          sanctionsScreeningSources: nextSettings.sanctionsScreening.sources,
          kycLinkValidityDays: Number(orgSettingsModal.kycLinkValidityDays),
          documentUploadLinkValidityDays: Number(orgSettingsModal.documentUploadLinkValidityDays),
          documentsLaterLinkValidityDays: Number(orgSettingsModal.documentsLaterLinkValidityDays),
          kycLinkReminderDays: Number(orgSettingsModal.kycLinkReminderDays),
          documentUploadLinkReminderDays: Number(orgSettingsModal.documentUploadLinkReminderDays),
          documentsLaterLinkReminderDays: Number(orgSettingsModal.documentsLaterLinkReminderDays),
          kycLinkSecondReminderDays: Number(orgSettingsModal.kycLinkSecondReminderDays),
          documentUploadLinkSecondReminderDays: Number(orgSettingsModal.documentUploadLinkSecondReminderDays),
          documentsLaterLinkSecondReminderDays: Number(orgSettingsModal.documentsLaterLinkSecondReminderDays),
        },
      });

      if (settingsResult.nameEntryMode !== orgSettingsModal.nameEntryMode) {
        throw new Error("The name-entry API update must be deployed before this setting can be saved");
      }
      await updateDoc(doc(db, "orgs", orgSettingsModal.orgId), {
        ...Object.fromEntries(REGULATORY_ADD_ONS.map(({ key }) => [
          `settings.regulatoryAddOns.${key}`, nextSettings.regulatoryAddOns[key],
        ])),
        "settings.regulatoryAddOns.updatedAt": serverTimestamp(),
        "settings.adverseMediaScreening.enhancedEnabled": nextSettings.adverseMediaScreening.enhancedEnabled,
        "settings.adverseMediaScreening.capAllegationsAtMedium": nextSettings.adverseMediaScreening.capAllegationsAtMedium,
        "settings.adverseMediaScreening.resultsPerQuery": nextSettings.adverseMediaScreening.resultsPerQuery,
        "settings.adverseMediaScreening.articlesPerSubject": nextSettings.adverseMediaScreening.articlesPerSubject,
        "settings.adverseMediaScreening.updatedAt": serverTimestamp(),
        "settings.pepScreening.aiProvider": nextSettings.pepScreening.aiProvider,
        "settings.pepScreening.updatedAt": serverTimestamp(),
        "settings.documentsRequest.individuals": nextSettings.documentsRequest.individuals,
        "settings.documentsRequest.individualsCv": nextSettings.documentsRequest.individualsCv,
        "settings.documentsRequest.entities": nextSettings.documentsRequest.entities,
        "settings.documentsRequest.entitiesDocTypes": nextSettings.documentsRequest.entitiesDocTypes,
        "settings.documentsRequest.maxPages": nextSettings.documentsRequest.maxPages,
        "settings.documentsRequest.mode": nextSettings.documentsRequest.mode,
        "settings.documentsRequest.updatedAt": serverTimestamp(),
        "settings.complianceAssessment.enabled":
          nextSettings.complianceAssessment.enabled,
        "settings.complianceAssessment.updatedAt": serverTimestamp(),
        "settings.documentDataConsistencyCheck.scanIdEnabled":
          nextSettings.documentDataConsistencyCheck.scanIdEnabled,
        "settings.documentDataConsistencyCheck.updatedAt": serverTimestamp(),
        "settings.proofOfAddressVerification.enabled":
          nextSettings.proofOfAddressVerification.enabled,
        "settings.proofOfAddressVerification.validityUnlimited":
          nextSettings.proofOfAddressVerification.validityUnlimited,
        "settings.proofOfAddressVerification.maxAgeDays":
          nextSettings.proofOfAddressVerification.maxAgeDays,
        "settings.proofOfAddressVerification.issuerCheckEnabled":
          nextSettings.proofOfAddressVerification.issuerCheckEnabled,
        "settings.proofOfAddressVerification.updatedAt": serverTimestamp(),
        "settings.corporateDocumentVerification.enabled":
          nextSettings.corporateDocumentVerification.enabled,
        "settings.corporateDocumentVerification.validityUnlimited":
          nextSettings.corporateDocumentVerification.validityUnlimited,
        "settings.corporateDocumentVerification.maxAgeDays":
          nextSettings.corporateDocumentVerification.maxAgeDays,
        "settings.corporateDocumentVerification.updatedAt": serverTimestamp(),
        "settings.sanctionsScreening.sources":
          nextSettings.sanctionsScreening.sources,
        "settings.sanctionsScreening.updatedAt": serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setOrgs((prev) =>
        (prev || []).map((org) =>
          org.orgId === orgSettingsModal.orgId
            ? {
                ...org,
                nameEntryMode: orgSettingsModal.nameEntryMode,
                settings: {
                  ...(org.settings || {}),
                  regulatoryAddOns: {
                    ...(org.settings?.regulatoryAddOns || {}),
                    ...nextSettings.regulatoryAddOns,
                  },
                  adverseMediaScreening: {
                    ...(org.settings?.adverseMediaScreening || {}),
                    ...nextSettings.adverseMediaScreening,
                  },
                  pepScreening: {
                    ...(org.settings?.pepScreening || {}),
                    ...nextSettings.pepScreening,
                  },
                  documentsRequest: {
                    ...(org.settings?.documentsRequest || {}),
                    ...nextSettings.documentsRequest,
                  },
                  complianceAssessment: {
                    ...(org.settings?.complianceAssessment || {}),
                    ...nextSettings.complianceAssessment,
                  },
                  documentDataConsistencyCheck: {
                    ...(org.settings?.documentDataConsistencyCheck || {}),
                    ...nextSettings.documentDataConsistencyCheck,
                  },
                  proofOfAddressVerification: {
                    ...(org.settings?.proofOfAddressVerification || {}),
                    ...nextSettings.proofOfAddressVerification,
                  },
                  corporateDocumentVerification: {
                    ...(org.settings?.corporateDocumentVerification || {}),
                    ...nextSettings.corporateDocumentVerification,
                  },
                  sanctionsScreening: {
                    ...(org.settings?.sanctionsScreening || {}),
                    ...nextSettings.sanctionsScreening,
                  },
                  ...Object.fromEntries(CLIENT_LINK_VALIDITY_FIELDS.map(({ field, reminderField, secondReminderField, settingKey }) => [settingKey, {
                    ...(org.settings?.[settingKey] || {}),
                    validityDays: Number(orgSettingsModal[field]),
                    reminderDays: Number(orgSettingsModal[reminderField]),
                    secondReminderDays: Number(orgSettingsModal[secondReminderField]),
                  }])),
                },
              }
            : org
        )
      );

      toastRef.current?.show({
        severity: "success",
        summary: "Settings saved",
        detail: `${orgSettingsModal.name} settings updated.`,
        life: 2500,
      });

      setOrgSettingsModal(null);
      await loadOrgs();
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Save failed",
        detail: e?.message || "Failed to save organisation settings",
        life: 4500,
      });
    } finally {
      setSavingOrgSettings(false);
    }
  }

  function getTeamOptionsForOrg(targetOrgId, excludeUid = "") {
    const selectedOrgId = String(targetOrgId || "").trim();
    const excluded = String(excludeUid || "").trim();
    const byTeam = new Map();

    for (const u of users || []) {
      if (!isStaffCategory(u.role)) continue;
      if (excluded && String(u.uid || "").trim() === excluded) continue;
      if (selectedOrgId && String(u.orgId || "").trim() !== selectedOrgId) continue;

      const teamId = String(u.teamId || "").trim();
      if (!teamId) continue;

      const teamName =
        String(u.teamName || "").trim() ||
        `${String(u.fullName || u.email || "Staff").trim()}'s team`;
      const current = byTeam.get(teamId) || {
        value: teamId,
        label: teamName,
        teamName,
        staffCount: 0,
      };

      current.staffCount += 1;
      byTeam.set(teamId, current);
    }

    return Array.from(byTeam.values()).sort((a, b) =>
      a.teamName.localeCompare(b.teamName)
    );
  }

  function getStaffOptionsForOrg(targetOrgId, excludeUid = "") {
    const selectedOrgId = String(targetOrgId || "").trim();
    const excluded = String(excludeUid || "").trim();

    return (users || [])
      .filter((u) => {
        if (!isStaffCategory(u.role)) return false;
        if (excluded && String(u.uid || "").trim() === excluded) return false;
        return !selectedOrgId || String(u.orgId || "").trim() === selectedOrgId;
      })
      .map((u) => {
        const fullName = String(u.fullName || u.displayName || "").trim();
        const email = String(u.email || "").trim();
        return {
          value: u.uid,
          uid: u.uid,
          label: fullName || email || u.uid,
          fullName,
          email,
          teamId: String(u.teamId || "").trim(),
          teamName: String(u.teamName || "").trim(),
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  function closeEditUserModal() {
    setEditUserModal(null);
    setReassignModal(null);
  }

  function openEditUserModal(user, { keepReassign = false } = {}) {
    const cleanRole = normalizeRole(user?.role) || ROLES.STAFF_FRONT;
    const cleanTeamId = String(user?.teamId || "").trim();
    if (!keepReassign) setReassignModal(null);
    setEditUserModal({
      uid: user?.uid || "",
      email: user?.email || "",
      fullName: user?.fullName || "",
      role: cleanRole,
      orgId: user?.orgId || "",
      teamMode: isStaffCategory(cleanRole) && cleanTeamId ? "existing" : "none",
      teamId: cleanTeamId,
      teamName: user?.teamName || "",
      disabled: !!user?.disabled,
    });
  }

  async function handleSaveUserEdit() {
    if (!editUserModal || savingUserEdit) return;

    const cleanName = String(editUserModal.fullName || "").trim();
    const cleanRole = normalizeRole(editUserModal.role);
    const isStaffRole = isStaffCategory(cleanRole);

    if (!cleanRole) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Role required",
        detail: "Please choose a role for this user.",
        life: 3000,
      });
      return;
    }

    if (!cleanName) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Name required",
        detail: "Please enter a full name.",
        life: 3000,
      });
      return;
    }

    if (
      isStaffRole &&
      editUserModal.teamMode === "existing" &&
      !String(editUserModal.teamId || "").trim()
    ) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Select team",
        detail: "Please select an existing team.",
        life: 3000,
      });
      return;
    }

    if (
      isStaffRole &&
      editUserModal.teamMode === "new" &&
      !String(editUserModal.teamName || "").trim()
    ) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Team name required",
        detail: "Please enter a team name.",
        life: 3000,
      });
      return;
    }

    setSavingUserEdit(true);
    try {
      const token = await getTokenOrThrow();
      await apiFetch("/myKyc/admin/update-user", {
        method: "POST",
        token,
        body: {
          uid: String(editUserModal.uid || ""),
          fullName: cleanName,
          role: cleanRole,
          keepTeam: true,
          disabled: !!editUserModal.disabled,
        },
      });

      if (reassignModal?.user?.uid === editUserModal.uid) {
        await handleSaveReassign();
        return;
      }

      toastRef.current?.show({
        severity: "success",
        summary: "User updated",
        detail: "Staff member access has been saved.",
        life: 3000,
      });

      setEditUserModal(null);
      await loadUsers();
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Update failed",
        detail: e?.message || "Failed to update user",
        life: 4500,
      });
    } finally {
      setSavingUserEdit(false);
    }
  }

  async function openReassignModal(user) {
    const cleanUid = String(user?.uid || "").trim();
    const userOrgId = String(user?.orgId || "").trim();
    if (!cleanUid || !userOrgId || !isStaffCategory(user?.role)) return;

    try {
      const ownSnap = await getDocs(
        query(
          collection(db, "clients"),
          where("orgId", "==", userOrgId),
          where("accessScope", "==", "individual"),
          where("ownerUid", "==", cleanUid)
        )
      );
      const ownClients = ownSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const oldTeamId = String(user?.teamId || "").trim();
      let oldTeamClients = [];
      let remainingOldTeamStaff = 0;
      if (oldTeamId) {
        const teamSnap = await getDocs(
          query(
            collection(db, "clients"),
            where("orgId", "==", userOrgId),
            where("accessScope", "==", "team"),
            where("teamId", "==", oldTeamId)
          )
        );
        oldTeamClients = teamSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        remainingOldTeamStaff = (users || []).filter(
          (u) =>
            isStaffCategory(u.role) &&
            String(u.orgId || "").trim() === userOrgId &&
            String(u.teamId || "").trim() === oldTeamId &&
            String(u.uid || "").trim() !== cleanUid
        ).length;
      }

      const currentMode = oldTeamId ? "existing" : "none";
      const currentTeamOption = oldTeamId
        ? {
            value: oldTeamId,
            label: user.teamName || "Current team",
            teamName: user.teamName || "Current team",
          }
        : null;

      setReassignModal({
        user,
        ownClients,
        oldTeamClients,
        remainingOldTeamStaff,
      });
      setEditUserModal(null);
      setReassignTeamMode(currentMode);
      setReassignTeamName("");
      setReassignTeamOption(currentTeamOption);
      setReassignMoveMode(
        ownClients.length > 0 || (oldTeamId && remainingOldTeamStaff === 0 && oldTeamClients.length > 0)
          ? "affected_clients"
          : "staff_only"
      );
      setReassignSelectedClientIds([]);
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Cannot load reassignment",
        detail: e?.message || "Failed to inspect client access.",
        life: 4500,
      });
    }
  }

  async function handleSaveReassign() {
    if (!reassignModal || savingReassign) return;

    if (reassignTeamMode === "new" && !String(reassignTeamName || "").trim()) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Team name required",
        detail: "Please enter a team name.",
        life: 3000,
      });
      return;
    }

    if (reassignTeamMode === "existing" && !reassignTeamOption?.value) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Select team",
        detail: "Please select an existing team.",
        life: 3000,
      });
      return;
    }

    const impact = getReassignImpact();
    if (reassignMoveMode === "staff_only" && impact.orphanRiskCount > 0) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Client orphan risk",
        detail: "Move affected or selected clients before saving.",
        life: 4000,
      });
      return;
    }

    if (reassignMoveMode === "own_clients" && !ownClientsCoverOrphanRisk(impact)) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Client orphan risk",
        detail: "Choose an option that includes all orphan-risk clients.",
        life: 4000,
      });
      return;
    }

    if (reassignMoveMode === "selected_clients" && reassignSelectedClientIds.length === 0) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Select clients",
        detail: "Choose at least one client to move.",
        life: 3000,
      });
      return;
    }

    if (
      reassignMoveMode === "selected_clients" &&
      !selectedClientsCoverOrphanRisk(impact, reassignSelectedClientIds)
    ) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Include orphan-risk clients",
        detail: "Please include all orphan-risk clients before saving.",
        life: 4000,
      });
      return;
    }

    try {
      setSavingReassign(true);
      const token = await getTokenOrThrow();
      const res = await apiFetch("/myKyc/admin/reassign-staff", {
        method: "POST",
        token,
        body: {
          uid: String(reassignModal.user?.uid || ""),
          teamMode: reassignTeamMode,
          teamId: reassignTeamMode === "existing" ? reassignTeamOption?.value || "" : "",
          teamName:
            reassignTeamMode === "new"
              ? String(reassignTeamName || "").trim()
              : reassignTeamMode === "existing"
              ? reassignTeamOption?.teamName || reassignTeamOption?.label || ""
              : "",
          moveMode: reassignMoveMode,
          selectedClientIds: reassignSelectedClientIds,
        },
      });

      toastRef.current?.show({
        severity: "success",
        summary: "Staff reassigned",
        detail: `Moved ${res.movedClientCount || 0} client(s).`,
        life: 3500,
      });

      setReassignModal(null);
      setEditUserModal(null);
      await loadUsers();
      await loadClientCounts(orgs);
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Reassignment failed",
        detail: e?.message || "Failed to reassign staff",
        life: 5000,
      });
    } finally {
      setSavingReassign(false);
    }
  }

  async function openDeleteClientReassignModal(user, impact = null) {
    const cleanUid = String(user?.uid || "").trim();
    const userOrgId = String(user?.orgId || impact?.orgId || "").trim();
    if (!cleanUid || !userOrgId) return;

    try {
      const ownSnap = await getDocs(
        query(
          collection(db, "clients"),
          where("orgId", "==", userOrgId),
          where("accessScope", "==", "individual"),
          where("ownerUid", "==", cleanUid)
        )
      );
      const ownClients = ownSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const oldTeamId = String(user?.teamId || impact?.oldTeamId || "").trim();
      const remainingOldTeamStaff = Number(impact?.remainingOldTeamStaff || 0);
      let oldTeamClients = [];
      if (oldTeamId && remainingOldTeamStaff === 0) {
        const teamSnap = await getDocs(
          query(
            collection(db, "clients"),
            where("orgId", "==", userOrgId),
            where("accessScope", "==", "team"),
            where("teamId", "==", oldTeamId)
          )
        );
        oldTeamClients = teamSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }

      const affectedClients = [
        ...new Map([...ownClients, ...oldTeamClients].map((client) => [client.id, client])).values(),
      ];
      const staffOptions = getStaffOptionsForOrg(userOrgId, cleanUid);
      const teamOptions = getTeamOptionsForOrg(userOrgId, cleanUid);

      setDeleteClientReassignModal({
        user: { ...user, orgId: userOrgId },
        impact: {
          ...(impact || {}),
          ownClientCount: impact?.ownClientCount ?? ownClients.length,
          oldTeamClientCount: impact?.oldTeamClientCount ?? oldTeamClients.length,
          remainingOldTeamStaff,
          orphanRiskCount: impact?.orphanRiskCount ?? affectedClients.length,
        },
        affectedClients,
      });
      setDeleteClientTargetMode(staffOptions.length > 0 ? "staff" : teamOptions.length > 0 ? "team" : "delete");
      setDeleteClientTargetStaff(null);
      setDeleteClientTargetTeam(null);
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Cannot load clients",
        detail: e?.message || "Failed to inspect clients before deletion.",
        life: 4500,
      });
    }
  }

  async function resolveDeleteClientReassignment(action) {
    if (!deleteClientReassignModal || resolvingDeleteClients) return;

    const isReassign = action === "reassign";
    if (isReassign && deleteClientTargetMode === "staff" && !deleteClientTargetStaff?.value) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Select staff",
        detail: "Please choose the staff member who will receive these clients.",
        life: 3000,
      });
      return;
    }
    if (isReassign && deleteClientTargetMode === "team" && !deleteClientTargetTeam?.value) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Select team",
        detail: "Please choose the team that will receive these clients.",
        life: 3000,
      });
      return;
    }

    try {
      setResolvingDeleteClients(true);
      const token = await getTokenOrThrow();
      const res = await apiFetch("/myKyc/admin/resolve-staff-delete-orphans", {
        method: "POST",
        token,
        body: {
          uid: String(deleteClientReassignModal.user?.uid || ""),
          action,
          targetType: deleteClientTargetMode,
          targetUid: deleteClientTargetMode === "staff" ? deleteClientTargetStaff?.value || "" : "",
          targetTeamId: deleteClientTargetMode === "team" ? deleteClientTargetTeam?.value || "" : "",
        },
      });

      toastRef.current?.show({
        severity: "success",
        summary: "Staff deleted",
        detail:
          action === "delete_clients"
            ? `Deleted ${res.affectedClientCount || 0} orphan-risk client(s) and removed the staff member.`
            : `Reassigned ${res.affectedClientCount || 0} client(s) and removed the staff member.`,
        life: 4000,
      });

      setDeleteClientReassignModal(null);
      await loadUsers();
      await loadClientCounts(orgs);
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: action === "delete_clients" ? "Delete failed" : "Reassignment failed",
        detail: e?.message || "Failed to resolve clients before deleting staff.",
        life: 5000,
      });
    } finally {
      setResolvingDeleteClients(false);
    }
  }

  function confirmDeleteOrphanClients() {
    const count = deleteClientReassignModal?.affectedClients?.length || 0;
    appConfirm({
      group: "appConfirm",
      header: "Delete orphan-risk clients",
      message: `Delete ${count} client(s) and then delete this staff member? This cannot be undone.`,
      acceptLabel: "Delete orphan(s)",
      rejectLabel: "Cancel",
      scheme: "danger",
      icon: "bi bi-exclamation-triangle",
      accept: () => resolveDeleteClientReassignment("delete_clients"),
      reject: () => {},
    });
  }

  async function setUserDisabled(uid, disabled) {
    try {
      const token = await getTokenOrThrow();

      await apiFetch("/myKyc/admin/set-user-disabled", {
        method: "POST",
        token,
        body: { uid, disabled: !!disabled },
      });

      toastRef.current?.show({
        severity: "success",
        summary: disabled ? "User deactivated" : "User reactivated",
        detail: disabled
          ? "Access blocked for this account."
          : "Access restored for this account.",
        life: 2800,
      });

      await loadUsers();
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Update failed",
        detail: e?.message || "Failed to update user",
        life: 4500,
      });
    }
  }

  // ✅ Disable / Reactivate organisation
  async function setOrgDisabled(orgIdToUpdate, disabled) {
    try {
      const token = await getTokenOrThrow();

      await apiFetch("/myKyc/admin/set-org-disabled", {
        method: "POST",
        token,
        body: { orgId: String(orgIdToUpdate), disabled: !!disabled },
      });

      toastRef.current?.show({
        severity: "success",
        summary: disabled
          ? "Organisation disabled"
          : "Organisation reactivated",
        detail: disabled
          ? "All associated users will be blocked (soft block)."
          : "Access restored for this organisation.",
        life: 3200,
      });

      await loadOrgs();
      await loadUsers();
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Org update failed",
        detail: e?.message || "Failed to update organisation",
        life: 4500,
      });
    }
  }

  // ✅ Delete organisation + all associated users and data
  function handleDeleteOrg(orgId, orgName) {
    const orgUsers = (users || []).filter((u) => String(u.orgId || "").trim() === String(orgId));
    const userCount = orgUsers.length;
    const clientCount = clientCountByOrgId[orgId] ?? "?";

    appConfirm({
      group: "appConfirm",
      header: "Delete organisation",
      message: `Permanently delete "${orgName || orgId}" and ALL associated data?\n\n• ${userCount} user(s)\n• ${clientCount} client(s) + all KYC, submissions, uploads\n\nThis cannot be undone.`,
      acceptLabel: "Delete everything",
      rejectLabel: "Cancel",
      scheme: "danger",
      icon: "bi bi-exclamation-triangle",
      accept: async () => {
        try {
          const token = await getTokenOrThrow();

          // 1. Delete all associated users via API
          for (const u of orgUsers) {
            await apiFetch("/myKyc/admin/delete-user", {
              method: "POST",
              token,
              body: { uid: String(u.uid) },
            });
          }

          // 2. Purge all Firestore + Storage data for this org
          await apiFetch("/myKyc/admin/purge-org-data", {
            method: "POST",
            token,
            body: { orgId: String(orgId), deleteOrgSettings: true },
          });

          // 3. Delete the org document itself
          await apiFetch("/myKyc/admin/delete-org", {
            method: "POST",
            token,
            body: { orgId: String(orgId) },
          });

          toastRef.current?.show({
            severity: "success",
            summary: "Organisation deleted",
            detail: `Deleted org, ${userCount} user(s), and all associated client data.`,
            life: 4000,
          });

          await loadOrgs();
          await loadUsers();
        } catch (e) {
          toastRef.current?.show({
            severity: "error",
            summary: "Delete failed",
            detail: e?.message || "Failed to delete organisation",
            life: 4500,
          });
        }
      },
      reject: () => {},
    });
  }

  // ✅ Delete user
  function handleDeleteUser(userOrUid, maybeEmail) {
    const user =
      typeof userOrUid === "object" && userOrUid !== null
        ? userOrUid
        : users.find((u) => String(u.uid || "").trim() === String(userOrUid || "").trim()) || {
            uid: userOrUid,
            email: maybeEmail,
          };
    const uid = String(user?.uid || "").trim();
    const email = user?.email || maybeEmail;

    appConfirm({
      group: "appConfirm",
      header: "Delete user",
      message: `Are you sure you want to delete ${email || uid}? This cannot be undone.`,
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      scheme: "danger",
      icon: "bi bi-exclamation-triangle",
      accept: async () => {
        try {
          const token = await getTokenOrThrow();

          await apiFetch("/myKyc/admin/delete-user", {
            method: "POST",
            token,
            body: { uid: String(uid) },
          });

          toastRef.current?.show({
            severity: "success",
            summary: "User deleted",
            detail: "The user account has been removed.",
            life: 3000,
          });

          await loadUsers();
        } catch (e) {
          if (e?.code === "ORPHAN_RISK") {
            await openDeleteClientReassignModal(user, e?.impact);
            return;
          }

          toastRef.current?.show({
            severity: "error",
            summary: "Delete failed",
            detail: e?.message || "Failed to delete user",
            life: 4500,
          });
        }
      },
      reject: () => {},
    });
  }

  // ✅ Delete all users with no name (bot/empty accounts)
  function handleDeleteEmptyUsers() {
    const emptyUsers = users.filter(
      (u) => !u.fullName || !u.fullName.trim() || u.fullName.trim() === "(no name)"
    );
    if (emptyUsers.length === 0) {
      toastRef.current?.show({
        severity: "info",
        summary: "Nothing to delete",
        detail: "No users with an empty name were found.",
        life: 3000,
      });
      return;
    }

    appConfirm({
      group: "appConfirm",
      header: "Delete empty users",
      message: `Delete ${emptyUsers.length} user(s) with no name? This cannot be undone.`,
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      scheme: "danger",
      icon: "bi bi-person-x-fill",
      accept: () => runDeleteEmptyUsers(emptyUsers),
      reject: () => {},
    });
  }

  async function runDeleteEmptyUsers(emptyUsers) {
    setDeletionProgress({ done: 0, total: emptyUsers.length, finished: false, error: null });

    try {
      for (let i = 0; i < emptyUsers.length; i++) {
        // fresh cached token each iteration (no forced refresh)
        const token = await auth.currentUser?.getIdToken(false);
        if (!token) throw new Error("You must be signed in.");

        // one retry on transient network failure
        let attempts = 0;
        while (true) {
          try {
            await apiFetch("/myKyc/admin/delete-user", {
              method: "POST",
              token,
              body: { uid: String(emptyUsers[i].uid) },
            });
            break;
          } catch (e) {
            if (attempts++ < 1 && e?.message === "Failed to fetch") {
              await new Promise((r) => setTimeout(r, 800));
            } else {
              throw e;
            }
          }
        }

        setDeletionProgress({ done: i + 1, total: emptyUsers.length, finished: false, error: null });

        // small pause to avoid overwhelming the server
        if (i < emptyUsers.length - 1) {
          await new Promise((r) => setTimeout(r, 150));
        }
      }

      setDeletionProgress((p) => ({ ...p, finished: true }));
      await loadUsers();
    } catch (e) {
      setDeletionProgress((p) => ({ ...p, finished: true, error: e?.message || "Delete failed" }));
    }
  }

  // ✅ Resend activation
  async function handleResendActivation(uid, email) {
    try {
      const token = await getTokenOrThrow();

      await apiFetch("/myKyc/admin/resend-activation", {
        method: "POST",
        token,
        body: {
          uid: String(uid),
          email: String(email || "")
            .trim()
            .toLowerCase(),
        },
      });

      toastRef.current?.show({
        severity: "success",
        summary: "Activation sent",
        detail: `Activation link resent to ${email}`,
        life: 3200,
      });
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Resend failed",
        detail: e?.message || "Failed to resend activation",
        life: 4500,
      });
    }
  }

  // ✅ build quick map for org name + disabled badge
  const orgById = useMemo(() => {
    const map = {};
    for (const o of orgs || []) {
      if (o?.orgId) map[String(o.orgId)] = o;
    }
    return map;
  }, [orgs]);

  // ✅ react-select options
  const orgOptions = useMemo(() => {
    return (orgs || []).map((o) => ({
      value: o.orgId,
      label: o.name ? `${o.name}` : o.orgId,
      orgId: o.orgId,
      name: o.name || "",
      disabled: !!o.disabled,
    }));
  }, [orgs]);

  const teamOptions = useMemo(() => {
    return getTeamOptionsForOrg(orgId);
  }, [users, orgId]);

  const editUserTeamOptions = useMemo(() => {
    if (!editUserModal) return [];
    return getTeamOptionsForOrg(editUserModal.orgId, editUserModal.uid);
  }, [users, editUserModal?.orgId, editUserModal?.uid]);

  const reassignTeamOptions = useMemo(() => {
    if (!reassignModal) return [];
    return getTeamOptionsForOrg(reassignModal.user?.orgId, reassignModal.user?.uid);
  }, [users, reassignModal?.user?.orgId, reassignModal?.user?.uid]);

  const deleteClientStaffOptions = useMemo(() => {
    if (!deleteClientReassignModal) return [];
    return getStaffOptionsForOrg(
      deleteClientReassignModal.user?.orgId,
      deleteClientReassignModal.user?.uid
    );
  }, [users, deleteClientReassignModal?.user?.orgId, deleteClientReassignModal?.user?.uid]);

  const deleteClientTeamOptions = useMemo(() => {
    if (!deleteClientReassignModal) return [];
    return getTeamOptionsForOrg(
      deleteClientReassignModal.user?.orgId,
      deleteClientReassignModal.user?.uid
    );
  }, [users, deleteClientReassignModal?.user?.orgId, deleteClientReassignModal?.user?.uid]);

  function getReassignImpact() {
    const ownClients = reassignModal?.ownClients || [];
    const oldTeamClients = reassignModal?.oldTeamClients || [];
    const oldTeamId = String(reassignModal?.user?.teamId || "").trim();
    const remainingOldTeamStaff = Number(reassignModal?.remainingOldTeamStaff || 0);
    const orphanRiskClients = [
      ...ownClients,
      ...(oldTeamId && remainingOldTeamStaff === 0 ? oldTeamClients : []),
    ];
    const allMovable = [...new Map(
      [...ownClients, ...oldTeamClients].map((c) => [c.id, c])
    ).values()];

    return {
      ownClients,
      oldTeamClients,
      oldTeamId,
      remainingOldTeamStaff,
      orphanRiskClients,
      allMovable,
      orphanRiskCount: orphanRiskClients.length,
    };
  }

  function ownClientsCoverOrphanRisk(impact) {
    if (!impact?.orphanRiskCount) return true;
    const ownIds = new Set((impact.ownClients || []).map((client) => client.id));
    return (impact.orphanRiskClients || []).every((client) => ownIds.has(client.id));
  }

  function selectedClientsCoverOrphanRisk(impact, selectedClientIds) {
    if (!impact?.orphanRiskCount) return true;
    const selectedIds = new Set(selectedClientIds || []);
    return (impact.orphanRiskClients || []).every((client) => selectedIds.has(client.id));
  }

  function renderReassignContent() {
    if (!reassignModal) return null;

    const impact = getReassignImpact();
    const selectedSet = new Set(reassignSelectedClientIds);
    const ownClientsDisabled =
      impact.ownClients.length === 0 || !ownClientsCoverOrphanRisk(impact);
    const targetOptions = [
      { value: "none", label: "No team" },
      { value: "new", label: "New team" },
      {
        value: "existing",
        label: "Existing team",
        isDisabled: reassignTeamOptions.length === 0,
      },
    ];
    const staffOnlyDisabled = impact.orphanRiskCount > 0;

    return (
      <div className="reassignInlinePanel">
        <div className="mb-3">
          <label className="form-label small text-muted mb-1">
            Team access
          </label>
          <Select
            value={
              targetOptions.find((o) => o.value === reassignTeamMode) ||
              targetOptions[0]
            }
            options={targetOptions}
            isSearchable={false}
            styles={selectStyles}
            onChange={(opt) => {
              const mode = opt?.value || "none";
              setReassignTeamMode(mode);
              setReassignTeamName("");
              setReassignTeamOption(null);
            }}
          />
        </div>

        {reassignTeamMode === "new" && (
          <div className="mb-3">
            <label className="form-label small text-muted mb-1">
              Team name
            </label>
            <input
              className="form-control"
              value={reassignTeamName}
              onChange={(e) => setReassignTeamName(e.target.value)}
              placeholder={`${reassignModal.user?.fullName || "Staff"}'s team`}
            />
          </div>
        )}

        {reassignTeamMode === "existing" && (
          <div className="mb-3">
            <label className="form-label small text-muted mb-1">
              Existing team
            </label>
            <Select
              value={reassignTeamOption}
              options={reassignTeamOptions}
              isSearchable
              isClearable
              placeholder="Search team..."
              styles={selectStyles}
              onChange={setReassignTeamOption}
              formatOptionLabel={(opt) => (
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <span>{opt.teamName || opt.label}</span>
                  <span style={{ fontSize: 11, color: "#8a96a3" }}>
                    {opt.staffCount} staff
                  </span>
                </div>
              )}
            />
          </div>
        )}

        <div className="reassignImpactBox">
          <div>Own clients: {impact.ownClients.length}</div>
          <div>Current team clients: {impact.oldTeamClients.length}</div>
          <div>
            Remaining staff in current team: {impact.remainingOldTeamStaff}
          </div>
          {impact.orphanRiskCount > 0 && (
            <div className="reassignRiskText">
              Orphan risk: {impact.orphanRiskCount} client(s)
            </div>
          )}
        </div>

        <div className="reassignMoveOptions">
          <label className={staffOnlyDisabled ? "is-disabled" : ""}>
            <input
              type="radio"
              name="reassignMoveMode"
              checked={reassignMoveMode === "staff_only"}
              disabled={staffOnlyDisabled}
              onChange={() => setReassignMoveMode("staff_only")}
            />
            <span>Move staff only</span>
          </label>

          <label className={ownClientsDisabled ? "is-disabled" : ""}>
            <input
              type="radio"
              name="reassignMoveMode"
              checked={reassignMoveMode === "own_clients"}
              disabled={ownClientsDisabled}
              onChange={() => setReassignMoveMode("own_clients")}
            />
            <span>
              Move staff + staff&apos;s own clients ({impact.ownClients.length} clients)
            </span>
          </label>

          {impact.orphanRiskCount > 0 && (
            <label>
              <input
                type="radio"
                name="reassignMoveMode"
                checked={reassignMoveMode === "affected_clients"}
                onChange={() => setReassignMoveMode("affected_clients")}
              />
              <span>
                Move staff + orphan-risk clients ({impact.orphanRiskCount} clients)
              </span>
            </label>
          )}

          <label className={impact.allMovable.length === 0 ? "is-disabled" : ""}>
            <input
              type="radio"
              name="reassignMoveMode"
              checked={reassignMoveMode === "selected_clients"}
              disabled={impact.allMovable.length === 0}
              onChange={() => setReassignMoveMode("selected_clients")}
            />
            <span>Choose clients to move</span>
          </label>
        </div>

        {impact.orphanRiskCount > 0 && (
          <div className="reassignDisabledHint">
            Disabled options would leave {impact.orphanRiskCount} client(s) without staff/team access.
          </div>
        )}

        {reassignMoveMode === "selected_clients" && (
          <div className="reassignClientList">
            {impact.allMovable.map((client) => (
              <label key={client.id} className="reassignClientRow">
                <input
                  type="checkbox"
                  checked={selectedSet.has(client.id)}
                  onChange={(e) => {
                    setReassignSelectedClientIds((prev) =>
                      e.target.checked
                        ? [...new Set([...prev, client.id])]
                        : prev.filter((id) => id !== client.id)
                    );
                  }}
                />
                <span>{client.clientName || client.name || client.id}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  }

  const teamAccessLabel = useMemo(() => {
    if (teamMode === "new") return teamName || "New team";
    if (teamMode === "existing") {
      return selectedTeamOption?.teamName || selectedTeamOption?.label || "Existing team";
    }
    return "No team";
  }, [teamMode, teamName, selectedTeamOption]);

  const teamAccessOptions = useMemo(() => {
    return TEAM_ACCESS_OPTIONS.map((option) =>
      option.value === "existing" && teamOptions.length === 0
        ? { ...option, isDisabled: true }
        : option
    );
  }, [teamOptions.length]);

  const teamAccessValue = useMemo(() => {
    return {
      ...(teamAccessOptions.find((o) => o.value === teamMode) || teamAccessOptions[0]),
      label: teamAccessLabel,
    };
  }, [teamAccessOptions, teamMode, teamAccessLabel]);

  function openTeamAccessModal(mode) {
    if (mode === "none") {
      setTeamMode("none");
      setTeamName("");
      setSelectedTeamOption(null);
      setTeamAccessModal(null);
      return;
    }

    if (mode === "existing" && !orgId) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Select organisation",
        detail: "Select an organisation before choosing an existing team.",
        life: 3000,
      });
      return;
    }

    if (mode === "existing" && teamOptions.length === 0) {
      toastRef.current?.show({
        severity: "warn",
        summary: "No existing team",
        detail: "This organisation has no shared team yet.",
        life: 3000,
      });
      return;
    }

    setPendingTeamName(
      mode === "new"
        ? teamName || (fullName.trim() ? `${fullName.trim()}'s team` : "")
        : ""
    );
    setPendingTeamOption(mode === "existing" ? selectedTeamOption : null);
    setTeamAccessModal(mode);
  }

  function applyTeamAccessModal() {
    if (teamAccessModal === "new") {
      const cleanName = String(pendingTeamName || "").trim();
      if (!cleanName) {
        toastRef.current?.show({
          severity: "warn",
          summary: "Team name required",
          detail: "Please enter a team name.",
          life: 3000,
        });
        return;
      }
      setTeamMode("new");
      setTeamName(cleanName);
      setSelectedTeamOption(null);
      setTeamAccessModal(null);
      return;
    }

    if (teamAccessModal === "existing") {
      if (!pendingTeamOption?.value) {
        toastRef.current?.show({
          severity: "warn",
          summary: "Select team",
          detail: "Please select an existing team.",
          life: 3000,
        });
        return;
      }
      setTeamMode("existing");
      setSelectedTeamOption(pendingTeamOption);
      setTeamName("");
      setTeamAccessModal(null);
    }
  }

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const orgQ = orgSearch.trim().toLowerCase();
    const selectedOrgId = String(highlightedOrgId || "").trim();

    return (users || []).filter((u) => {
      if (selectedOrgId && String(u.orgId || "").trim() !== selectedOrgId) {
        return false;
      }

      const name = (u.fullName || "").toLowerCase();
      const mail = (u.email || "").toLowerCase();
      const r = (u.role || "").toLowerCase();
      const org = (u.orgId || "").toLowerCase();
      const orgName = String(orgById[u.orgId]?.name || "").toLowerCase();

      if (orgQ && !org.includes(orgQ) && !orgName.includes(orgQ)) {
        return false;
      }

      if (!q) return true;

      return (
        name.includes(q) ||
        mail.includes(q) ||
        r.includes(q) ||
        org.includes(q) ||
        orgName.includes(q)
      );
    });
  }, [users, search, orgSearch, orgById, highlightedOrgId]);

  const filteredOrgs = useMemo(() => {
    const q = orgSearch.trim().toLowerCase();
    if (!q) return orgs;

    return orgs.filter((o) => {
      const id = String(o.orgId || "").toLowerCase();
      const name = String(o.name || "").toLowerCase();
      return id.includes(q) || name.includes(q);
    });
  }, [orgs, orgSearch]);

  // ✅ react-select minimal styling so it looks like bootstrap input (without changing your page style)
  const selectStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        height: "35px",
        minHeight: "35px",
        borderRadius: "0.375rem",
        borderColor: "#ced4da",
        boxShadow: "none",
        fontSize: "14px",
      }),
      valueContainer: (base) => ({
        ...base,
        height: "35px",
        padding: "0 12px",
      }),
      // ✅ placeholder text (Search org…)
      placeholder: (base) => ({
        ...base,
        color: "#a5acb2",
        fontWeight: 300,
        fontSize: "14px",
      }),
      // ✅ text of the selected value (inside the input)
      singleValue: (base) => ({
        ...base,
        color: "#4c665e",
        // fontWeight: 100,
        letterSpacing: "-0.01em",
        fontSize: "14px",
      }),
      // ✅ the text you type while searching
      input: (base) => ({
        ...base,
        color: "#4c665e",
        margin: 0,
        padding: 0,
        fontSize: "14px",
      }),
      indicatorsContainer: (base) => ({
        ...base,
        height: "35px",
      }),
      // ✅ on hover etc options
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? "rgba(155,175,165,0.25)" // selected option bg
          : state.isFocused
          ? "rgba(155,175,165,0.15)" // ✅ hover bg
          : "transparent",
        color: "#2f3a4a",
        cursor: "pointer",
        fontSize: "14px",
        opacity: state.isDisabled ? 0.45 : 1,
        cursor: state.isDisabled ? "not-allowed" : "pointer",
      }),
      indicatorSeparator: () => ({ display: "none" }),
      menu: (base) => ({
        ...base,
        zIndex: 50,
      }),
    }),
    []
  );

  {
    /* ************************ PURGE ALL FUNCTION  ************************** */
  }

  // ------ REUSABLE DELETE ALL CLIENTS FUNCTION --------

  async function handlePurgeAll() {
    appConfirm({
      group: "appConfirm",
      header: "Delete ALL data",
      message:
        "This will permanently delete ALL clients + ALL KYC forms + ALL KYC submissions. This cannot be undone.",
      defaultFocus: "reject",
      acceptLabel: "Delete everything",
      rejectLabel: "Cancel",
      scheme: "danger",
      icon: "bi bi-exclamation-triangle",
      accept: async () => {
        try {
          const res = await purgeAllClientsAndKyc(db);

          toastRef.current?.show({
            severity: "success",
            summary: "Database cleared",
            detail: `Deleted ${res.deletedClients ?? 0} clients, ${res.deletedForms ?? 0} KYC forms, ${res.deletedSubmissions ?? 0} submissions, ${res.deletedDrafts ?? 0} drafts, ${res.deletedInvites ?? 0} invites, ${res.deletedDocSubmissions ?? 0} doc submissions, ${res.deletedDocInvites ?? 0} doc invites, ${res.deletedEmailLogs ?? 0} email logs.`,
            life: 4000,
          });

          // toastRef.current?.show({
          //   severity: "success",
          //   summary: "Database cleared",
          //   detail: `Deleted ${res.deletedClients} clients, ${res.deletedForms} KYC forms, ${res.deletedSubmissions} KYC submissions, ${res.res.deletedInvites} KYC invitations.`,
          //   life: 4000,
          // });

          // ✅ refresh UI if needed
          // loadClients();
          // loadKycInboxCount();
        } catch (e) {
          toastRef.current?.show({
            severity: "error",
            summary: "Error",
            detail: e?.message || "Purge failed",
            life: 4000,
          });
        }
      },
    });
  }

  async function handlePurgeOrg() {
    const orgLabel = orgOptions?.find((o) => o.value === orgId)?.name || orgId;
    appConfirm({
      group: "appConfirm",
      header: "Delete org data",
      message: `Delete ALL clients + KYC data for "${orgLabel}"? This cannot be undone.`,
      defaultFocus: "reject",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      scheme: "danger",
      icon: "bi bi-exclamation-triangle",
      accept: async () => {
        try {
          const token = await getTokenOrThrow();
          const res = await apiFetch("/myKyc/admin/purge-org-data", {
            method: "POST",
            token,
            body: { orgId: String(orgId) },
          });
          toastRef.current?.show({
            severity: "success",
            summary: "Org data cleared",
            detail: `Deleted ${res.clientsPurged ?? 0} clients and related KYC/document data.`,
            life: 4000,
          });
          await loadClientCounts(orgs);
        } catch (e) {
          toastRef.current?.show({
            severity: "error",
            summary: "Error",
            detail: e?.message || "Purge failed",
            life: 4000,
          });
        }
      },
    });
  }

  function handleDeleteOrgClients(org) {
    const targetOrgId = String(org?.orgId || "").trim();
    if (!targetOrgId) return;

    const orgLabel = org?.name || targetOrgId;
    const clientCount = clientCountByOrgId[targetOrgId] ?? "?";

    appConfirm({
      group: "appConfirm",
      header: "Delete organisation clients",
      message: `Delete ALL clients and related KYC/document data for "${orgLabel}"?\n\n• ${clientCount} client(s)\n\nThe organisation and users will NOT be deleted.\n\nThis cannot be undone.`,
      defaultFocus: "reject",
      acceptLabel: "Delete clients",
      rejectLabel: "Cancel",
      scheme: "danger",
      icon: "bi bi-exclamation-triangle",
      accept: async () => {
        try {
          setPurgingOrgClientsId(targetOrgId);
          const token = await getTokenOrThrow();
          const res = await apiFetch("/myKyc/admin/purge-org-data", {
            method: "POST",
            token,
            body: { orgId: targetOrgId },
          });

          toastRef.current?.show({
            severity: "success",
            summary: "Organisation clients deleted",
            detail: `Deleted ${res.clientsPurged ?? 0} clients and related KYC/document data.`,
            life: 4000,
          });

          await loadClientCounts(orgs);
        } catch (e) {
          toastRef.current?.show({
            severity: "error",
            summary: "Delete clients failed",
            detail: e?.message || "Failed to delete organisation clients",
            life: 4500,
          });
        } finally {
          setPurgingOrgClientsId("");
        }
      },
    });
  }

  {
    /* ************************ SEED 50 CLIENTS FUNCTION  ************************** */
  }
  const [seeding, setSeeding] = useState(false);
  const [seedTeamModal, setSeedTeamModal] = useState(null);
  const [selectedSeedTeamOption, setSelectedSeedTeamOption] = useState(null);

  const selectedSeedTeamOptions = useMemo(() => {
    const targetOrgId = String(seedTeamModal?.orgId || "").trim();
    if (!targetOrgId) return [];

    const byTeam = new Map();
    const ownStaffOptions = [];
    for (const u of users || []) {
      if (!isStaffCategory(u.role)) continue;
      if (String(u.orgId || "").trim() !== targetOrgId) continue;

      const teamId = String(u.teamId || "").trim();
      if (!teamId) {
        const uid = String(u.uid || "").trim();
        if (!uid) continue;
        const staffName = String(u.fullName || u.email || "Staff").trim();
        ownStaffOptions.push({
          value: `own:${uid}`,
          label: `${staffName} · own clients`,
          targetType: "own",
          teamId: "",
          teamName: "",
          ownerUid: uid,
          createdByUid: uid,
          staffName,
          staffCount: 1,
        });
        continue;
      }

      const teamName =
        String(u.teamName || "").trim() ||
        `${String(u.fullName || u.email || "Staff").trim()}'s team`;
      const current = byTeam.get(teamId) || {
        value: teamId,
        label: teamName,
        targetType: "team",
        teamId,
        teamName,
        createdByUid: String(u.uid || "").trim(),
        staffCount: 0,
      };
      current.staffCount += 1;
      byTeam.set(teamId, current);
    }

    const teamOptions = Array.from(byTeam.values()).sort((a, b) =>
      a.teamName.localeCompare(b.teamName)
    );
    ownStaffOptions.sort((a, b) => a.staffName.localeCompare(b.staffName));

    return [...teamOptions, ...ownStaffOptions];
  }, [seedTeamModal, users]);

  async function handleSeedClients() {
    // ✅ must seed into a real orgId
    const targetOrgId = String(orgId || "").trim();

    if (!targetOrgId) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Select an organisation",
        detail:
          "Please select an organisation first (so the seeded clients belong to that org).",
        life: 3500,
      });
      return;
    }

    // ✅ block seeding into disabled org
    const targetOrg = orgById[targetOrgId];
    if (targetOrg?.disabled) {
      toastRef.current?.show({
        severity: "error",
        summary: "Organisation disabled",
        detail:
          "You cannot seed clients into a disabled organisation. Reactivate it first.",
        life: 4000,
      });
      return;
    }

    const orgStaffCount = (users || []).filter(
      (u) =>
        isStaffCategory(u.role) &&
        String(u.orgId || "").trim() === targetOrgId
    ).length;

    if (orgStaffCount === 0) {
      toastRef.current?.show({
        severity: "warn",
        summary: "No staff member",
        detail: "Create a staff member in this organisation before seeding clients.",
        life: 4500,
      });
      return;
    }

    setSelectedSeedTeamOption(null);
    setSeedTeamModal({
      orgId: targetOrgId,
      orgName: targetOrg?.name || targetOrgId,
    });
  }

  async function runSeedClientsForTeam() {
    if (seeding) return;
    const targetOrgId = String(seedTeamModal?.orgId || "").trim();
    const targetOrgName = String(seedTeamModal?.orgName || targetOrgId).trim();
    const teamId =
      selectedSeedTeamOption?.targetType === "own"
        ? ""
        : String(selectedSeedTeamOption?.teamId || selectedSeedTeamOption?.value || "").trim();
    const teamName = String(
      selectedSeedTeamOption?.teamName || ""
    ).trim();
    const createdByUid = String(selectedSeedTeamOption?.createdByUid || "").trim();
    const ownerUid = String(selectedSeedTeamOption?.ownerUid || "").trim();
    const targetLabel = String(
      selectedSeedTeamOption?.label || teamName || "selected target"
    ).trim();

    if (!targetOrgId || (!teamId && !ownerUid)) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Select a target",
        detail: "Please choose a team or a no-team staff member for the demo clients.",
        life: 3500,
      });
      return;
    }

    try {
      setSeeding(true);

      const res = await seedSampleClients(db, {
        orgId: targetOrgId,
        teamId,
        teamName,
        ownerUid,
        createdByUid,
        count: 50,
      });

      if (res.alreadySeeded) {
        toastRef.current?.show({
          severity: "info",
          summary: "Already seeded",
          detail: `Demo clients already exist for ${targetLabel} (seedTag: ${res.seedTag}).`,
          life: 4000,
        });
        return;
      }

      toastRef.current?.show({
        severity: "success",
        summary: "Seed completed",
        detail: `Inserted ${res.inserted} demo clients into "${targetOrgName}" for ${targetLabel}.`,
        life: 4000,
      });

      setSeedTeamModal(null);
      setSelectedSeedTeamOption(null);
      await loadClientCounts(orgs);
    } catch (e) {
      toastRef.current?.show({
        severity: "error",
        summary: "Seed failed",
        detail: e?.message || "Failed to seed demo clients",
        life: 4500,
      });
    } finally {
      setSeeding(false);
    }
  }
  // -------- TODO HANDLERS --------

  async function openTodoModal() {
    setShowTodoModal(true);
    setTodoLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, "adminTodos"), orderBy("createdAt", "asc"))
      );
      const allTodos = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const now = Date.now();
      const stale = allTodos.filter((t) => {
        if (!t.completed || !t.completedAt) return false;
        const ms = t.completedAt.toMillis ? t.completedAt.toMillis() : 0;
        return now - ms > TEN_DAYS_MS;
      });
      await Promise.all(stale.map((t) => deleteDoc(doc(db, "adminTodos", t.id))));
      setTodos(allTodos.filter((t) => !stale.some((s) => s.id === t.id)));
    } catch (e) {
      console.error("Failed to load todos:", e);
    } finally {
      setTodoLoading(false);
    }
  }

  async function handleAddTodo(category) {
    const text = (todoInputs[category] || "").trim();
    if (!text) return;
    const priority = todoPriority[category] || "normal";
    setTodoSaving((prev) => ({ ...prev, [category]: true }));
    try {
      const ref = await addDoc(collection(db, "adminTodos"), {
        text,
        category,
        priority,
        completed: false,
        completedAt: null,
        createdAt: serverTimestamp(),
        createdByUid: auth.currentUser?.uid || null,
      });
      setTodos((prev) => [
        ...prev,
        { id: ref.id, text, category, priority, completed: false, completedAt: null },
      ]);
      setTodoInputs((prev) => ({ ...prev, [category]: "" }));
      setTodoPriority((prev) => ({ ...prev, [category]: "normal" }));
    } catch (e) {
      console.error("Failed to add todo:", e);
    } finally {
      setTodoSaving((prev) => ({ ...prev, [category]: false }));
    }
  }

  async function handleToggleTodo(todo) {
    const nowDone = !todo.completed;
    setTodos((prev) =>
      prev.map((t) =>
        t.id === todo.id
          ? { ...t, completed: nowDone, completedAt: nowDone ? { toMillis: () => Date.now() } : null }
          : t
      )
    );
    try {
      await updateDoc(doc(db, "adminTodos", todo.id), {
        completed: nowDone,
        completedAt: nowDone ? serverTimestamp() : null,
      });
    } catch (e) {
      console.error("Failed to toggle todo:", e);
      setTodos((prev) => prev.map((t) => (t.id === todo.id ? todo : t)));
    }
  }

  async function handleDeleteTodo(todoId) {
    setTodos((prev) => prev.filter((t) => t.id !== todoId));
    try {
      await deleteDoc(doc(db, "adminTodos", todoId));
    } catch (e) {
      console.error("Failed to delete todo:", e);
    }
  }

  async function handleMoveToTop(todo) {
    const topOrder = Date.now();
    setTodos((prev) =>
      prev.map((t) => (t.id === todo.id ? { ...t, topOrder } : t))
    );
    try {
      await updateDoc(doc(db, "adminTodos", todo.id), { topOrder });
    } catch (e) {
      console.error("Failed to move to top:", e);
      setTodos((prev) => prev.map((t) => (t.id === todo.id ? todo : t)));
    }
  }

  {
    /* ********************************************************************* */
  }
  {
    /* ******************** RETURN FUNCTION (RENDER) *********************** */
  }
  {
    /* ********************************************************************* */
  }

  return (
    <motion.div
      variants={pageAnim}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.25 }}
      className="adminUsersPage"
    >
      {/* TOP BAR */}
      <div className="adminUsersTitleBar">
        <div className="adminUsersContentWrap py-3">
          <div className="clientsHeader d-flex align-items-center justify-content-between">
            <div>
              <h2 className="clientsHeaderTitle m-0">
                ADMIN DASHBOARD · Users
              </h2>
              <div className="small" style={{ color: "#fff", opacity: 0.8 }}>
                Create staff accounts, manage access, and organisations
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={() => nav("/clients")}
                style={{ whiteSpace: "nowrap" }}
              >
                ← Back
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={openTodoModal}
              >
                <i className="bi bi-check2-square me-1" aria-hidden="true" />
                TODO
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={handleSeedClients}
                disabled={seeding}
              >
                {seeding ? "Seeding..." : "Seed 50"}
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={handlePurgeAll}
              >
                Purge All
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={handlePurgeOrg}
                disabled={!orgId}
                title={orgId ? `Purge data for org: ${orgId}` : "Select an org first"}
              >
                Purge Org
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={handleLogout}
                style={{ whiteSpace: "nowrap" }}
              >
                <i className="bi bi-box-arrow-right me-2" aria-hidden="true" />
                Log out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* CONTENT */}
      <div className="adminUsersContentWrap py-4">
        {err && <div className="alert alert-danger">{err}</div>}

        {/* ********************************************************************* */}
        {/* ********************✅ ORGANISATIONS CARD ✅************************** */}
        {/* ********************************************************************* */}

        <div className="card shadow-sm mb-3 card-top">
          {/* ********************************** */}
          <div className="p-3 border-bottom top-card-header d-flex align-items-center justify-content-between gap-2 flex-wrap">
            <div>
              <div
                className="fw-bold title-band-card"
                style={{ color: "#4C5465" }}
              >
                Organisations
              </div>
              <div className="small text-muted">
                {orgs.length} total organisation(s)
                {Object.keys(clientCountByOrgId).length > 0 && (
                  <span className="ms-2">
                    ·{" "}
                    {Object.values(clientCountByOrgId).reduce((a, b) => a + (b ?? 0), 0)} client(s)
                  </span>
                )}
              </div>
              {orgErr && (
                <div className="small" style={{ color: "#b42318" }}>
                  {orgErr}
                </div>
              )}
            </div>

            <div className="d-flex align-items-center gap-2">
              <div style={{ minWidth: 240 }}>
                <SearchBar
                  value={orgSearch}
                  onChange={setOrgSearch}
                  onClear={() => setOrgSearch("")}
                  placeholder="Search organisations…"
                  className="clientsSearch"
                />
              </div>

              <button
                className="btn btn-sm btn-activation"
                onClick={() => setShowCreateOrgModal(true)}
              >
                <i className="bi bi-plus-lg me-2" />
                New organisation
              </button>

              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={loadOrgs}
                disabled={orgLoading}
              >
                <i className="bi bi-arrow-repeat me-2" />
                Refresh
              </button>
            </div>
          </div>

          {/* ********************************** */}

          <div className="adminUsersTableWrap table-responsive">
            <table className="table adminUsersTable orgAdminTable align-middle m-0">
              <thead className="band-within-table">
                <tr className="titles-th-table">
                  <th className="ps-3">Name</th>
                  <th className="org-id-col">Org ID</th>
                  <th className="org-geo-col">Geo</th>
                  <th className="org-jurisdiction-col">Zone</th>
                  <th className="org-industry-col">Industry</th>
                  <th className="org-type-col">Type</th>
                  <th className="org-subscription-col">SUBSC</th>
                  <th className="org-count-col">Users</th>
                  <th className="org-count-col">Clients</th>
                  <th className="text-end pe-2 org-actions-col"></th>
                </tr>
              </thead>

              <tbody>
                {orgLoading && (
                  <tr>
                    <td colSpan={10} className="p-4 text-muted">
                      Loading…
                    </td>
                  </tr>
                )}

                {!orgLoading && filteredOrgs.length === 0 && (
                  <tr>
                    <td colSpan={10} className="p-4 text-muted">
                      No organisations found.
                    </td>
                  </tr>
                )}

                {!orgLoading &&
                  filteredOrgs.map((o) => {
                    const isOrgDisabled = !!o.disabled;
                    const isPurgingOrgClients = purgingOrgClientsId === o.orgId;
                    const hasNoClients = clientCountByOrgId[o.orgId] === 0;

                    return (
                      <tr
                        key={o.orgId}
                        className={highlightedOrgId === o.orgId ? "org-row-selected" : ""}
                      >
                        <td className="ps-3 users-list-text users-list-name">
                          {o.name || "—"}
                        </td>

                        <td className="org-id-col">
                          <span className="badge users-list-badge-role">
                            {o.orgId || "—"}
                          </span>
                          {isOrgDisabled && (
                            <span className="badge text-bg-danger ms-2">
                              Disabled
                            </span>
                          )}
                        </td>

                        <td
                          className="org-meta-cell org-geo-col"
                          onClick={() => openEditOrgModal(o)}
                        >
                          {o.geo ? (
                            <span className={`badge org-badge-geo org-geo-${o.geo}`}>
                              {o.geo === "uae" ? "UAE" : o.geo === "switzerland" ? "CH" : o.geo}
                            </span>
                          ) : <span className="text-muted org-meta-empty">—</span>}
                        </td>

                        <td
                          className="org-meta-cell org-jurisdiction-col"
                          onClick={() => openEditOrgModal(o)}
                        >
                          {o.geo === "uae" ? (
                            o.jurisdiction ? (
                              <span className="badge org-badge-jurisdiction">
                                {orgJurisdictionLabel(o.jurisdiction)}
                              </span>
                            ) : (
                              <span className="badge org-badge-jurisdiction org-badge-jurisdiction-empty">
                                Not set
                              </span>
                            )
                          ) : <span className="text-muted org-meta-empty">—</span>}
                        </td>

                        <td
                          className="org-meta-cell org-industry-col"
                          onClick={() => openEditOrgModal(o)}
                        >
                          {o.industry ? (
                            <span className="badge org-badge-industry">
                              {o.industry === "real_estate" ? "Real Estate" : o.industry.charAt(0).toUpperCase() + o.industry.slice(1)}
                            </span>
                          ) : <span className="text-muted org-meta-empty">—</span>}
                        </td>

                        <td
                          className="org-meta-cell org-type-col"
                          onClick={() => openEditOrgModal(o)}
                        >
                          {o.type ? (
                            <span className={`badge org-badge-type org-type-${String(o.type).toLowerCase()}`}>
                              {String(o.type).toUpperCase()}
                            </span>
                          ) : <span className="text-muted org-meta-empty">—</span>}
                        </td>

                        <td
                          className="org-meta-cell org-subscription-col"
                          onClick={() => openEditOrgModal(o)}
                        >
                          {o.subscription ? (
                            <span className={`badge org-badge-subscription org-subscription-${String(o.subscription).toLowerCase()}`}>
                              {orgSubscriptionLabel(o.subscription)}
                            </span>
                          ) : <span className="text-muted org-meta-empty">—</span>}
                        </td>

                        <td className="users-list-text users-list-email org-count-cell org-count-col">
                          {(() => {
                            const total = userCountByOrgId[o.orgId] || 0;
                            const label =
                              total === 0
                                ? "no user"
                                : total === 1
                                ? "1 user"
                                : `${total} users`;

                            return (
                              <>
                                <span>{label}</span>

                                {disabledUserCountByOrgId[o.orgId] > 0 && (
                                  <span style={{ opacity: 0.7 }}>
                                    {" "}
                                    ({disabledUserCountByOrgId[o.orgId]}{" "}
                                    disabled)
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </td>

                        <td className="users-list-text users-list-email org-count-cell org-count-col">
                          {clientCountByOrgId[o.orgId] == null ? (
                            <span className="text-muted">—</span>
                          ) : (
                            <span>
                              {clientCountByOrgId[o.orgId] === 0
                                ? "no client"
                                : clientCountByOrgId[o.orgId] === 1
                                ? "1 client"
                                : `${clientCountByOrgId[o.orgId]} clients`}
                            </span>
                          )}
                        </td>

                        <td className="text-end pe-2 org-actions-col">
                          <div className="d-flex justify-content-end gap-1 flex-nowrap">
                            <button
                              className={`btn btn-sm btn-outline-lightgrey org-row-action-btn${highlightedOrgId === o.orgId ? " btn-unselect-org" : ""}`}
                              type="button"
                              onClick={() => {
                                if (o.disabled) {
                                  toastRef.current?.show({
                                    severity: "warn",
                                    summary: "Organisation disabled",
                                    detail:
                                      "You cannot assign a user to a disabled organisation.",
                                    life: 3500,
                                  });
                                  return;
                                }
                                const match = orgOptions.find(
                                  (x) => x.value === o.orgId
                                );
                                if (highlightedOrgId === o.orgId) {
                                  setHighlightedOrgId(null);
                                  setSelectedOrgOption(null);
                                  setOrgId("");
                                  return;
                                }
                                setSelectedOrgOption(match || null);
                                setOrgId(o.orgId);
                                setHighlightedOrgId(o.orgId);

                                toastRef.current?.show({
                                  severity: "info",
                                  summary: "Org selected",
                                  detail: `Org ID set to ${o.orgId}`,
                                  life: 2200,
                                });
                              }}
                            >
                              {highlightedOrgId !== o.orgId && <i className="bi bi-person-raised-hand me-1"></i>}
                              {highlightedOrgId === o.orgId ? "Selected" : "Select"}
                            </button>

                            <button
                              className="btn btn-sm btn-outline-lightgrey org-row-action-btn"
                              type="button"
                              title={`Documents request: ${documentsRequestSummary(documentsRequestConfig(o?.settings))}`}
                              onClick={() => openOrgSettingsModal(o)}
                            >
                              <i className="bi bi-gear me-1" />
                              Settings
                            </button>

                            {!isOrgDisabled ? (
                              <button
                                className="btn btn-sm btn-outline-red org-row-action-btn"
                                type="button"
                                onClick={() => setOrgDisabled(o.orgId, true)}
                              >
                                <i className="bi bi-slash-circle me-2" />
                                Deactivate
                              </button>
                            ) : (
                              <button
                                className="btn btn-sm btn-outline-green org-row-action-btn"
                                type="button"
                                onClick={() => setOrgDisabled(o.orgId, false)}
                              >
                                <i className="bi bi-check-circle me-2" />
                                Reactivate
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-outline-red org-row-action-btn"
                              type="button"
                              disabled={isPurgingOrgClients || hasNoClients}
                              title={hasNoClients ? "No clients to delete" : "Delete all clients for this organisation"}
                              onClick={() => handleDeleteOrgClients(o)}
                            >
                              <i className="bi bi-trash2 me-2" />
                              {isPurgingOrgClients ? "Deleting..." : "Clients"}
                            </button>
                            <button
                              className="btn btn-sm clientsBtn clientsBtnDeleteIcon"
                              type="button"
                              onClick={() => handleDeleteOrg(o.orgId, o.name)}
                            >
                              <i className="bi bi-trash2-fill" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card shadow-sm mb-3 card-top">
          <div className="p-3 border-bottom top-card-header d-flex align-items-center justify-content-between gap-2 flex-wrap">
            <div>
              <div
                className="fw-bold title-band-card"
                style={{ color: "#4C5465" }}
              >
                API usage &amp; cost
              </div>
              <div className="small text-muted">
                API usage and estimated costs by organisation
                <span className="ms-2">
                  · {screeningUsageTotals.total} total name(s)
                  · {screeningUsageTotals.totalMonth} this month
                  · {formatUsd(screeningUsageTotals.costMonthUsd)} month cost
                </span>
              </div>
              {screeningUsageErr && (
                <div className="small" style={{ color: "#b42318" }}>
                  {screeningUsageErr}
                </div>
              )}
            </div>

            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={loadScreeningUsage}
              disabled={screeningUsageLoading}
            >
              <i className="bi bi-arrow-repeat me-2" />
              Refresh
            </button>
          </div>

          <div className="adminUsersTableWrap table-responsive">
            <table className="table adminUsersTable screeningUsageTable align-middle m-0">
              <thead className="band-within-table">
                <tr className="titles-th-table">
                  <th className="ps-3" rowSpan={2}>Organisation</th>
                  <th className="text-center" colSpan={7}>Month</th>
                  <th className="text-center pe-3" colSpan={7}>Total</th>
                  <th className="text-end pe-2 screening-usage-actions-col" rowSpan={2}></th>
                </tr>
                <tr className="titles-th-table">
                  <th className="text-center">PEP</th>
                  <th className="text-center">SANCT</th>
                  <th className="text-center">ADV</th>
                  <th className="text-center">Other</th>
                  <th className="text-center">DOCS</th>
                  <th className="text-center screeningUsageTotalHeader">Total</th>
                  <th className="text-center pe-3 screeningUsageTotalHeader">Cost</th>
                  <th className="text-center">PEP</th>
                  <th className="text-center">SANCT</th>
                  <th className="text-center">ADV</th>
                  <th className="text-center">Other</th>
                  <th className="text-center">DOCS</th>
                  <th className="text-center screeningUsageTotalHeader">Total</th>
                  <th className="text-center pe-3 screeningUsageTotalHeader">Cost</th>
                </tr>
              </thead>

              <tbody>
                {screeningUsageLoading && (
                  <tr>
                    <td colSpan={16} className="p-4 text-muted">
                      Loading…
                    </td>
                  </tr>
                )}

                {!screeningUsageLoading && filteredScreeningUsageByOrg.length === 0 && (
                  <tr>
                    <td colSpan={16} className="p-4 text-muted">
                      No API usage recorded yet.
                    </td>
                  </tr>
                )}

                {!screeningUsageLoading &&
                  filteredScreeningUsageByOrg.map((row) => (
                    <tr key={row.key}>
                      <td className="ps-3 users-list-text users-list-name">
                        <div>{row.orgName || "—"}</div>
                        <span className="badge users-list-badge-role screeningUsageOrgIdBadge">
                          {row.orgId || "—"}
                        </span>
                      </td>
                      <td className="users-list-text text-center">{screeningUsageMetric(row.pepMonth, row.pepCostMonthUsd)}</td>
                      <td className="users-list-text text-center">{screeningUsageMetric(row.sanctionsMonth, row.sanctionsCostMonthUsd)}</td>
                      <td className="users-list-text text-center">{screeningUsageMetric(row.adverseMediaMonth, row.adverseMediaCostMonthUsd)}</td>
                      <td className="users-list-text text-center">{screeningUsageMetric(row.otherMonth, row.otherCostMonthUsd)}</td>
                      <td className="users-list-text text-center">{documentStorageMetric(row.storageDocsMonth, row.storageBytesMonth)}</td>
                      <td className="users-list-text text-center screeningUsageTotalCell">{row.totalMonth}</td>
                      <td className="users-list-text text-center pe-3 screeningUsageCostCell screeningUsageTotalCell">{formatUsd(row.costMonthUsd)}</td>
                      <td className="users-list-text text-center">{screeningUsageMetric(row.pep, row.pepCostUsd)}</td>
                      <td className="users-list-text text-center">{screeningUsageMetric(row.sanctions, row.sanctionsCostUsd)}</td>
                      <td className="users-list-text text-center">{screeningUsageMetric(row.adverseMedia, row.adverseMediaCostUsd)}</td>
                      <td className="users-list-text text-center">{screeningUsageMetric(row.other, row.otherCostUsd)}</td>
                      <td className="users-list-text text-center">{documentStorageMetric(row.storageDocs, row.storageBytes)}</td>
                      <td className="users-list-text text-center screeningUsageTotalCell">{row.total}</td>
                      <td className="users-list-text text-center pe-3 screeningUsageCostCell screeningUsageTotalCell">{formatUsd(row.costUsd)}</td>
                      <td className="text-end pe-2 screening-usage-actions-col">
                        <button
                          className="btn btn-sm clientsBtn clientsBtnDeleteIcon"
                          type="button"
                          title="Reset API usage for this organisation"
                          disabled={resettingScreeningUsageOrgId === row.orgId}
                          onClick={() => handleResetScreeningUsage(row)}
                        >
                          <i className="bi bi-trash2-fill" />
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>

              {!screeningUsageLoading && filteredScreeningUsageByOrg.length > 0 && (
                <tfoot>
                  <tr className="screeningUsageAllOrgsRow">
                    <td className="ps-3">All organisations</td>
                    <td className="text-center">{screeningUsageMetric(screeningUsageTotals.pepMonth, screeningUsageTotals.pepCostMonthUsd)}</td>
                    <td className="text-center">{screeningUsageMetric(screeningUsageTotals.sanctionsMonth, screeningUsageTotals.sanctionsCostMonthUsd)}</td>
                    <td className="text-center">{screeningUsageMetric(screeningUsageTotals.adverseMediaMonth, screeningUsageTotals.adverseMediaCostMonthUsd)}</td>
                    <td className="text-center">{screeningUsageMetric(screeningUsageTotals.otherMonth, screeningUsageTotals.otherCostMonthUsd)}</td>
                    <td className="text-center">{documentStorageMetric(screeningUsageTotals.storageDocsMonth, screeningUsageTotals.storageBytesMonth)}</td>
                    <td className="text-center screeningUsageTotalCell">{screeningUsageTotals.totalMonth}</td>
                    <td className="text-center pe-3 screeningUsageCostCell screeningUsageTotalCell">{formatUsd(screeningUsageTotals.costMonthUsd)}</td>
                    <td className="text-center">{screeningUsageMetric(screeningUsageTotals.pep, screeningUsageTotals.pepCostUsd)}</td>
                    <td className="text-center">{screeningUsageMetric(screeningUsageTotals.sanctions, screeningUsageTotals.sanctionsCostUsd)}</td>
                    <td className="text-center">{screeningUsageMetric(screeningUsageTotals.adverseMedia, screeningUsageTotals.adverseMediaCostUsd)}</td>
                    <td className="text-center">{screeningUsageMetric(screeningUsageTotals.other, screeningUsageTotals.otherCostUsd)}</td>
                    <td className="text-center">{documentStorageMetric(screeningUsageTotals.storageDocs, screeningUsageTotals.storageBytes)}</td>
                    <td className="text-center screeningUsageTotalCell">{screeningUsageTotals.total}</td>
                    <td className="text-center pe-3 screeningUsageCostCell screeningUsageTotalCell">{formatUsd(screeningUsageTotals.costUsd)}</td>
                    <td className="text-end pe-2 screening-usage-actions-col"></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        {/* ********************************************************************* */}
        {/* **********************✅ USERS CARD ✅*************************** */}
        {/* ********************************************************************* */}

        <div className="card shadow-sm mb-3 card-bottom">
          <div className="p-3 border-bottom top-card-header d-flex align-items-center justify-content-between gap-2 flex-wrap">
            <div>
              <div
                className="fw-bold title-band-card"
                style={{ color: "#4C5465" }}
              >
                Staff
              </div>
              <div className="small text-muted">
                {users.length} total user account(s)
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <div style={{ minWidth: 240 }}>
                <SearchBar
                  value={search}
                  onChange={setSearch}
                  onClear={() => setSearch("")}
                  placeholder="Search users…"
                  className="clientsSearch"
                />
              </div>

              <button
                className="btn btn-sm"
                onClick={handleDeleteEmptyUsers}
                disabled={loading}
                title="Delete all users with no name"
                style={{ color: "#C88194", borderColor: "#C88194" }}
              >
                <i className="bi bi-person-x-fill me-2" />
                Delete empty users
              </button>

              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                aria-label="Info about user roles"
                aria-haspopup="dialog"
                onClick={() => setShowRoleInfo(true)}
              >
                <i className="bi bi-info-circle me-1" aria-hidden="true" />
                Info
              </button>

              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={loadUsers}
                disabled={loading}
              >
                <i className="bi bi-arrow-repeat me-2" />
                Refresh
              </button>
            </div>
          </div>

          {/* ************************ */}

          <div className="p-3 create-user-box">
            <form onSubmit={handleCreateUser} className="row g-2 adminUsersCreateUserForm">
              <div className="adminUsersNameField">
                <label className="form-label small text-muted mb-1">
                  Full name
                </label>
                <input
                  className="form-control"
                  value={fullName}
                  onChange={(e) => setFullName(titleCaseWords(e.target.value))}
                  placeholder="e.g. John Smith"
                />
              </div>

              <div className="adminUsersEmailField">
                <label className="form-label small text-muted mb-1">
                  Email
                </label>
                <input
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                />
              </div>

              {/* ✅ (1) react-select for organisations */}
              <div className={isStaffCategory(role) ? "adminUsersOrgField" : "col-3"}>
                <label className="form-label small text-muted mb-1">
                  Organisation
                </label>

                <Select
                  value={selectedOrgOption}
                  options={orgOptions}
                  isSearchable
                  isClearable
                  placeholder="Search org…"
                  styles={selectStyles}
                  onChange={(opt) => {
                    // cleared
                    if (!opt) {
                      setSelectedOrgOption(null);
                      setOrgId("");
                      setHighlightedOrgId(null);
                      setSelectedTeamOption(null);
                      setTeamMode("none");
                      setTeamName("");
                      return;
                    }

                    // prevent selecting disabled org
                    if (opt.disabled) {
                      toastRef.current?.show({
                        severity: "warn",
                        summary: "Organisation disabled",
                        detail:
                          "You cannot assign a user to a disabled organisation.",
                        life: 3500,
                      });
                      setSelectedOrgOption(null);
                      setOrgId("");
                      setHighlightedOrgId(null);
                      setSelectedTeamOption(null);
                      setTeamMode("none");
                      setTeamName("");
                      return;
                    }

                    setSelectedOrgOption(opt);
                    setOrgId(opt.value);
                    setSelectedTeamOption(null);
                    setTeamMode("none");
                    setTeamName("");
                  }}
                  formatOptionLabel={(opt, { context }) => {
                    // context === "menu"  -> dropdown list
                    // context === "value" -> selected value shown in input

                    if (context === "value") {
                      // ✅ input field = ONLY org name
                      return (
                        <div style={{ fontWeight: 500 }}>
                          {opt.name || opt.label}
                        </div>
                      );
                    }

                    // ✅ dropdown list = name + orgId
                    return (
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="select-dropdown-org">
                          {opt.name || opt.orgId}
                          {opt.disabled ? (
                            <i className="bi bi-ban-fill select-org-badge-disabled"></i>
                          ) : null}
                        </div>

                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 100,
                            opacity: 1,
                            color: "#A6B4AE",
                          }}
                        >
                          {opt.orgId}
                        </div>
                      </div>
                    );
                  }}
                  // formatOptionLabel={(opt) => (
                  //   <div className="d-flex align-items-center justify-content-between">
                  //     <div className="select-dropdown-org" style={{}}>
                  //       {opt.name || opt.orgId}
                  //       {opt.disabled ? (
                  //         <i className="bi bi-ban-fill select-org-badge-disabled"></i>
                  //       ) : null}
                  //     </div>
                  //     <div
                  //       style={{
                  //         fontSize: 12,
                  //         fontWeight: 100,
                  //         opacity: 1,
                  //         color: "#BBC6C1",
                  //       }}
                  //     >
                  //       {opt.orgId}
                  //     </div>
                  //   </div>
                  // )}
                />
              </div>

              <div className="adminUsersRoleField">
                <label className="form-label small text-muted mb-1">Role</label>
                <Select
                  value={ROLE_OPTIONS.find((o) => o.value === role) || null}
                  options={ROLE_OPTIONS}
                  isSearchable={false}
                  styles={selectStyles}
                  onChange={(opt) => {
                    const nextRole = opt?.value || ROLES.STAFF_FRONT;
                    setRole(nextRole);
                    if (!isStaffCategory(nextRole)) {
                      setTeamMode("none");
                      setTeamName("");
                      setSelectedTeamOption(null);
                      setTeamAccessModal(null);
                    }
                  }}
                />
              </div>

              {isStaffCategory(role) && (
                <div className="adminUsersTeamAccessField">
                  <label className="form-label small text-muted mb-1">
                    Team access
                  </label>
                  <Select
                    value={teamAccessValue}
                    options={teamAccessOptions}
                    isSearchable={false}
                    isOptionDisabled={(option) => option.isDisabled}
                    styles={selectStyles}
                    onChange={(opt) => openTeamAccessModal(opt?.value || "none")}
                  />
                </div>
              )}

              <div className="adminUsersCreateField d-flex align-items-end">
                <button
                  type="submit"
                  className="btn btn-primary btn-activation w-100"
                  disabled={creating}
                >
                  {creating ? "Creating…" : "Create"}
                </button>
              </div>

              {/* <div className="col-2 d-flex justify-content-end mt-2"> */}
              {/* <div className="col-1 btn-create-user">
                <button
                  type="submit"
                  className="btn btn-primary btn-activation"
                  disabled={creating}
                >
                  {creating ? "Creating…" : "Create"}
                </button>
              </div> */}
            </form>
          </div>

          {/* ************************ */}

          <div className="adminUsersTableWrap table-responsive">
            <table className="table adminUsersTable staffAdminTable align-middle m-0">
              <thead className="band-within-table">
                <tr className="titles-th-table">
                  <th className="ps-3">Name</th>
                  <th>Email</th>
                  <th className="staff-compact-col">Role</th>
                  <th>Team</th>
                  <th>Organisation</th>
                  <th className="staff-compact-col">Org ID</th>
                  <th className="staff-compact-col">Status</th>
                  <th className="text-end pe-2 staff-actions-col"></th>
                </tr>
              </thead>

              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={8} className="p-4 text-muted">
                      Loading…
                    </td>
                  </tr>
                )}

                {!loading && filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-4 text-muted">
                      No users found.
                    </td>
                  </tr>
                )}

                {!loading &&
                  filteredUsers.map((u) => {
                    const isDisabled = !!u.disabled;
                    const org = orgById[u.orgId];
                    const orgName = org?.name || "—";
                    const orgIsDisabled = !!org?.disabled;

                    return (
                      <tr
                        key={u.uid || u.email}
                        className="staff-row-clickable"
                        onClick={() => openEditUserModal(u)}
                      >
                        <td className="ps-3 users-list-text users-list-name">
                          {u.fullName || "—"}
                        </td>

                        <td className="users-list-text users-list-email">
                          {u.email || "—"}
                        </td>

                        <td className="staff-compact-col">
                          <span className="badge users-list-badge-role">
                            {roleLabel(u.role)}
                          </span>
                        </td>

                        <td
                          className={`users-list-text users-list-org ${
                            isStaffCategory(u.role) ? "staff-team-cell" : ""
                          }`}
                          onClick={(e) => {
                            if (!isStaffCategory(u.role)) return;
                            e.stopPropagation();
                            openReassignModal(u);
                          }}
                        >
                          {u.teamName || (isStaffCategory(u.role) ? "No team" : "—")}
                        </td>

                        <td className="users-list-text users-list-org">
                          {orgName}
                        </td>

                        <td className="staff-compact-col">
                          {/* Org badge */}
                          <span className="badge users-list-badge-org">
                            {u.orgId || "—"}
                          </span>
                        </td>

                        <td className="staff-compact-col">
                          {(() => {
                            const isDisabled = !!u.disabled;
                            const isOnboarded = !!u.onboarded;

                            if (isDisabled) {
                              return (
                                <span className="badge text-bg-danger">
                                  Disabled
                                </span>
                              );
                            }

                            if (!isOnboarded) {
                              return (
                                <span className="badge text-bg-yellow">
                                  Onboarding
                                </span>
                              );
                            }

                            return (
                              <span className="badge text-bg-success">
                                Active
                              </span>
                            );
                          })()}
                          {/* {isDisabled ? (
                            <span className="badge text-bg-danger">
                              Disabled
                            </span>
                          ) : (
                            <span className="badge text-bg-success">
                              Active
                            </span>
                          )} */}
                          {/* disabled badge next to org badge */}
                          {orgIsDisabled && (
                            <span className="badge text-bg-danger ms-2">
                              Org disabled
                            </span>
                          )}
                        </td>

                        <td className="text-end pe-2 staff-actions-col">
                          <div
                            className="d-flex justify-content-end gap-1 flex-nowrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* ✅ (1) Resend Pswd change email (activation) */}
                            <button
                              className="btn btn-sm btn-outline-lightgrey staff-row-action-btn"
                              onClick={() =>
                                handleResendActivation(u.uid, u.email)
                              }
                              disabled={!u.email}
                              type="button"
                            >
                              <i className="bi bi-envelope me-2" />
                              Change Pswd
                            </button>
                            {/* ✅ (2) existing deactivate/reactivate */}
                            {!isDisabled ? (
                              <button
                                className="btn btn-sm btn-outline-red staff-row-action-btn"
                                onClick={() => setUserDisabled(u.uid, true)}
                              >
                                <i className="bi bi-slash-circle me-2" />
                                Deactivate
                              </button>
                            ) : (
                              <button
                                className="btn btn-sm btn-outline-green staff-row-action-btn"
                                onClick={() => setUserDisabled(u.uid, false)}
                              >
                                <i className="bi bi-check-circle me-2" />
                                Reactivate
                              </button>
                            )}

                            {/* ✅ (3) Delete user */}
                            <button
                              className="btn btn-sm clientsBtn clientsBtnDeleteIcon"
                              onClick={() => handleDeleteUser(u)}
                              type="button"
                            >
                              <i className="bi bi-trash2-fill" />
                              {/* Delete user */}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ✅ Create organisation modal */}
      {showCreateOrgModal && (
        <div className="deletionProgressBackdrop" onClick={() => setShowCreateOrgModal(false)}>
          <div className="createOrgModal" onClick={(e) => e.stopPropagation()}>
            <div className="deletionProgressIcon">
              <i className="bi bi-building" />
            </div>

            <span className="deletionProgressTitle">New organisation</span>

            <form onSubmit={handleCreateOrg} className="w-100">
              <div className="mb-3">
                <label className="form-label small text-muted mb-1">Name</label>
                <input
                  className="form-control"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  placeholder="e.g. Cronos Consulting"
                  autoFocus
                />
              </div>

              <div className="mb-3">
                <label className="form-label small text-muted mb-1">Geography</label>
                <select
                  className="form-select"
                  value={newOrgGeo}
                  onChange={(e) => setNewOrgGeo(e.target.value)}
                >
                  <option value="uae">UAE</option>
                  <option value="switzerland">Switzerland</option>
                </select>
              </div>

              {newOrgGeo === "uae" && (
                <div className="mb-3">
                  <label className="form-label small text-muted mb-1">Zone</label>
                  <select
                    className="form-select"
                    value={newOrgJurisdiction}
                    onChange={(e) => setNewOrgJurisdiction(e.target.value)}
                  >
                    {ORG_JURISDICTION_OPTIONS.map((option) => (
                      <option key={option.value || "not-set"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label small text-muted mb-1">Industry</label>
                <select
                  className="form-select"
                  value={newOrgIndustry}
                  onChange={(e) => setNewOrgIndustry(e.target.value)}
                >
                  <option value="finance">Finance</option>
                  <option value="real_estate">Real Estate</option>
                  <option value="csp">CSP</option>
                  <option value="legal">Legal</option>
                  <option value="accounting">Accounting</option>
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label small text-muted mb-1">Type</label>
                <select
                  className="form-select"
                  value={newOrgType}
                  onChange={(e) => setNewOrgType(e.target.value)}
                >
                  {ORG_TYPE_OPTIONS.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label small text-muted mb-1">Subscription</label>
                <select
                  className="form-select"
                  value={newOrgSubscription}
                  onChange={(e) => setNewOrgSubscription(e.target.value)}
                >
                  {ORG_SUBSCRIPTION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {orgErr && <p className="text-danger small mb-2">{orgErr}</p>}

              <div className="createOrgModalActions">
                <button type="submit" className="deletionProgressCloseBtn" style={{ background: "#90a7bb" }} disabled={creatingOrg}>
                  {creatingOrg ? "Creating…" : "Create"}
                </button>
                <button type="button" className="deletionProgressCloseBtn" style={{ background: "#b3b6bd" }} onClick={() => setShowCreateOrgModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Dialog
        visible={showRoleInfo}
        onHide={() => setShowRoleInfo(false)}
        header="User roles"
        className="adminRolesDialog"
        modal
        blockScroll
        dismissableMask
        draggable={false}
        resizable={false}
        footer={
          <button type="button" className="btn btn-outline-secondary" onClick={() => setShowRoleInfo(false)}>
            Close
          </button>
        }
      >
        <div className="table-responsive" role="region" aria-label="Role comparison" tabIndex={0}>
          <table className="table table-bordered align-middle adminRolesTable">
            <caption className="visually-hidden">Permissions available to each myKYC user role</caption>
            <thead>
              <tr>
                {["Capability", "Staff compliance", "Staff front", "Manager", "Admin"].map((heading) => (
                  <th key={heading} scope="col">{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROLE_COMPARISON.map(([capability, ...permissions]) => (
                <tr key={capability}>
                  <th scope="row">{capability}</th>
                  {permissions.map((permission, index) => <td key={index}>{permission}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="small text-muted mb-0 mt-3">
          Managers can access all teams within their organisation. Both staff categories share the same client access scope; Staff front sees compliance areas read-only.
        </p>
      </Dialog>

      {/* ✅ Edit org geo/industry modal */}
      {editOrgModal && (
        <div className="deletionProgressBackdrop" onClick={() => setEditOrgModal(null)}>
          <div className="createOrgModal" onClick={(e) => e.stopPropagation()}>
            <div className="deletionProgressIcon">
              <i className="bi bi-building-gear" />
            </div>

            <span className="deletionProgressTitle">{editOrgModal.name}</span>

            <div className="orgSettingsModalBody">
              <div className="mb-3">
                <label className="form-label small text-muted mb-1">Geography</label>
                <select
                  className="form-select"
                  value={editOrgModal.geo}
                  onChange={(e) => setEditOrgModal((m) => ({ ...m, geo: e.target.value }))}
                >
                  <option value="uae">UAE</option>
                  <option value="switzerland">Switzerland</option>
                </select>
              </div>

              {editOrgModal.geo === "uae" && (
                <div className="mb-3">
                  <label className="form-label small text-muted mb-1">Zone</label>
                  <select
                    className="form-select"
                    value={editOrgModal.jurisdiction}
                    onChange={(e) => setEditOrgModal((m) => ({ ...m, jurisdiction: e.target.value }))}
                  >
                    {ORG_JURISDICTION_OPTIONS.map((option) => (
                      <option key={option.value || "not-set"} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label small text-muted mb-1">Industry</label>
                <select
                  className="form-select"
                  value={editOrgModal.industry}
                  onChange={(e) => setEditOrgModal((m) => ({ ...m, industry: e.target.value }))}
                >
                  <option value="finance">Finance</option>
                  <option value="real_estate">Real Estate</option>
                  <option value="csp">CSP</option>
                  <option value="legal">Legal</option>
                  <option value="accounting">Accounting</option>
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label small text-muted mb-1">Type</label>
                <select
                  className="form-select"
                  value={editOrgModal.type}
                  onChange={(e) => setEditOrgModal((m) => ({ ...m, type: e.target.value }))}
                >
                  {ORG_TYPE_OPTIONS.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="form-label small text-muted mb-1">Subscription</label>
                <select
                  className="form-select"
                  value={editOrgModal.subscription}
                  onChange={(e) => setEditOrgModal((m) => ({ ...m, subscription: e.target.value }))}
                >
                  {ORG_SUBSCRIPTION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="createOrgModalActions">
                <button className="deletionProgressCloseBtn" style={{ background: "#90a7bb" }} onClick={handleSaveOrgMeta}>
                  Save
                </button>
                <button className="deletionProgressCloseBtn" style={{ background: "#b3b6bd" }} onClick={() => setEditOrgModal(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {orgSettingsModal && (
        <div className="deletionProgressBackdrop orgSettingsBackdrop" onClick={() => setOrgSettingsModal(null)}>
          <div className="createOrgModal orgSettingsModal" onClick={(e) => e.stopPropagation()}>
            {/* Inside the card: clicks in this (portaled) modal must not reach the backdrop and close the settings. */}
            <CorporateDocumentTypesModal open={docTypesInfoOpen} onClose={() => setDocTypesInfoOpen(false)} />
            <div className="orgSettingsModalHeader">
              <div className="deletionProgressIcon">
                <i className="bi bi-sliders" />
              </div>

              <span className="deletionProgressTitle">{orgSettingsModal.name}</span>
            </div>

            <div className="orgSettingsModalBody">
              <div className="orgSettingsSectionTitle">Individual names</div>
              <div className="orgSettingsOptions orgSettingsOptions--compact">
                {[{ value: "full", label: "Full name" }, { value: "separate", label: "First name and surname" }].map(option => (
                  <label key={option.value} className={`orgSettingsOption${orgSettingsModal.nameEntryMode === option.value ? " isSelected" : ""}`}>
                    <input type="radio" name="nameEntryMode" value={option.value}
                      checked={orgSettingsModal.nameEntryMode === option.value}
                      disabled={orgSettingsModal.nameEntryLocked || savingOrgSettings}
                      onChange={() => setOrgSettingsModal(previous => ({ ...previous, nameEntryMode: option.value }))} />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              <p className="small text-muted">{orgSettingsModal.nameEntryLocked
                ? "Locked because this organisation has records"
                : "This choice becomes permanent when the first client is created"}</p>
              <div className="orgSettingsSectionTitle">Documents request in the KYC form</div>
              {[
                { key: "individuals", label: "Individuals" },
                { key: "entities", label: "Entities" },
              ].map(({ key, label }) => (
                <div key={key} className="orgDocRequestRow">
                  <div className="orgDocRequestLabel">{label}</div>
                  <div className="orgDocRequestChoices" role="radiogroup" aria-label={`${label} documents request`}>
                    {DOCUMENT_REQUEST_TIMINGS.map((t) => (
                      <label key={t.value}
                        className={`orgSettingsOption${orgSettingsModal.documentsRequest?.[key] === t.value ? " is-selected" : ""}`}>
                        <input type="radio" name={`documentsRequest_${key}`} value={t.value}
                          checked={orgSettingsModal.documentsRequest?.[key] === t.value}
                          onChange={() => setOrgSettingsModal((m) => ({
                            ...m,
                            documentsRequest: { ...m.documentsRequest, [key]: t.value },
                          }))} />
                        <span>
                          {t.label}
                          <span className="orgDocRequestHint">{key === "entities" && t.value === "start"
                            ? "Upload the company documents first; company details are pre-filled"
                            : key === "individuals" && t.value === "start"
                              ? "Upload ID and proof of address first; details are pre-filled"
                              : t.hint}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                  {key === "individuals" ? (
                    <label className={`orgDocRequestExtra${orgSettingsModal.documentsRequest?.individuals !== "start" ? " is-disabled" : ""}`}>
                      <input type="checkbox" name="documentsRequest_individualsCv"
                        disabled={orgSettingsModal.documentsRequest?.individuals !== "start"}
                        checked={orgSettingsModal.documentsRequest?.individuals === "start" && orgSettingsModal.documentsRequest?.individualsCv === true}
                        onChange={(e) => setOrgSettingsModal((m) => ({
                          ...m,
                          documentsRequest: { ...m.documentsRequest, individualsCv: e.target.checked },
                        }))} />
                      <span>Also ask for a CV (only with "At the start")</span>
                    </label>
                  ) : (
                    <div className={`orgDocRequestScope${orgSettingsModal.documentsRequest?.entities === "none" ? " is-disabled" : ""}`}
                      role="group" aria-label="Entity documents requested">
                      <span className="orgDocRequestScopeTitle">
                        Documents (from the list of the country of incorporation)
                        <button type="button" className="corpReqInfoBtn" aria-label="About document types" title="About document types"
                          onClick={() => setDocTypesInfoOpen(true)}>
                          <i className="bi bi-info-circle" aria-hidden="true" />
                        </button>
                      </span>
                      {ENTITY_DOCUMENT_TYPES.map((t) => {
                        const types = entityDocTypes(orgSettingsModal.documentsRequest?.entitiesDocTypes);
                        const checked = types.includes(t.value);
                        const onlyOne = checked && types.length === 1;
                        return (
                          <label key={t.value} className={onlyOne ? "is-locked" : undefined}
                            title={onlyOne ? "At least one type is requested" : undefined}>
                            <input type="checkbox" name={`documentsRequest_entitiesDocTypes_${t.value}`}
                              checked={checked}
                              disabled={orgSettingsModal.documentsRequest?.entities === "none" || onlyOne}
                              onChange={(e) => setOrgSettingsModal((m) => {
                                const current = entityDocTypes(m.documentsRequest?.entitiesDocTypes);
                                const next = e.target.checked ? [...current, t.value] : current.filter((v) => v !== t.value);
                                return { ...m, documentsRequest: { ...m.documentsRequest, entitiesDocTypes: entityDocTypes(next) } };
                              })} />
                            <span>{t.label} <span className="orgDocRequestTypeHint">{t.hint}</span></span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
              <p className="small text-muted">
                Owners' and signatories' documents are requested separately, as before.
              </p>
              {/* Pages of an uploaded PDF the AI reads (the first ones); longer PDFs are not refused. */}
              <div className="orgDocRequestPages">
                <label className="form-label small fw-semibold mb-0" htmlFor="documentsRequest_maxPages">Pages read per uploaded PDF</label>
                <select id="documentsRequest_maxPages" name="documentsRequest_maxPages" className="form-select form-select-sm"
                  value={intakeMaxPages(orgSettingsModal.documentsRequest?.maxPages)}
                  onChange={(e) => setOrgSettingsModal((m) => ({ ...m, documentsRequest: { ...m.documentsRequest, maxPages: Number(e.target.value) } }))}>
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n === 1 ? "First page" : `First ${n} pages`}</option>)}
                </select>
                <span className="small text-muted">The AI reads only these pages of a longer PDF. More pages cost more per document.</span>
              </div>

              <div className="orgSettingsSectionTitle">Links sent to clients · validity & reminder</div>
              <div className="row g-3 mb-1">
                {CLIENT_LINK_VALIDITY_FIELDS.map(({ field, reminderField, secondReminderField, label }) => (
                  <div className="col-sm-4" key={field}>
                    <div className="small fw-semibold mb-2">{label}</div>
                    <label className="form-label small" htmlFor={field}>Link validity</label>
                    <select id={field} name={field} className="form-select mb-2"
                      value={orgSettingsModal[field]} disabled={savingOrgSettings}
                      onChange={event => {
                        const value = Number(event.target.value);
                        // A reminder must stay before expiry: reset it when the link becomes shorter.
                        setOrgSettingsModal(previous => ({
                          ...previous,
                          [field]: value,
                          [reminderField]: kycLinkReminderDays(previous[reminderField], value),
                        }));
                      }}>
                      {KYC_LINK_VALIDITY_DAY_OPTIONS.map(days => (
                        <option key={days} value={days}>{kycLinkValidityText(days)}</option>
                      ))}
                    </select>
                    <label className="form-label small" htmlFor={reminderField}>Reminder after</label>
                    <select id={reminderField} name={reminderField} className="form-select mb-2"
                      value={orgSettingsModal[reminderField]} disabled={savingOrgSettings}
                      onChange={event => {
                        const value = Number(event.target.value);
                        setOrgSettingsModal(previous => ({ ...previous, [reminderField]: value }));
                      }}>
                      <option value={0}>Off</option>
                      {KYC_LINK_VALIDITY_DAY_OPTIONS.filter(days => days < orgSettingsModal[field]).map(days => (
                        <option key={days} value={days}>{days === 1 ? "1 day" : `${days} days`}</option>
                      ))}
                    </select>
                    <label className="form-label small" htmlFor={secondReminderField}>2nd reminder after expiry</label>
                    <select id={secondReminderField} name={secondReminderField} className="form-select"
                      value={orgSettingsModal[secondReminderField]} disabled={savingOrgSettings}
                      onChange={event => {
                        const value = Number(event.target.value);
                        setOrgSettingsModal(previous => ({ ...previous, [secondReminderField]: value }));
                      }}>
                      <option value={0}>Off</option>
                      {KYC_LINK_VALIDITY_DAY_OPTIONS.map(days => (
                        <option key={days} value={days}>{days === 1 ? "1 day" : `${days} days`}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              <p className="small text-muted">
                How long the client can use each link after it is sent, when a reminder is emailed if it is still unused,
                and when a 2nd reminder with a new link is emailed after it has expired (reminders 9:00–18:00, organisation
                time). Links already sent keep their original settings.
              </p>

              <div className="orgSettingsSectionTitle">Compliance assessment</div>
              <div className="orgSettingsOptions orgSettingsOptions--compact">
                {[
                  { value: true, label: "Yes" },
                  { value: false, label: "No" },
                ].map((option) => (
                  <label
                    key={String(option.value)}
                    className={`orgSettingsOption${
                      orgSettingsModal.complianceAssessmentEnabled === option.value
                        ? " is-selected"
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="complianceAssessmentEnabled"
                      checked={
                        orgSettingsModal.complianceAssessmentEnabled ===
                        option.value
                      }
                      onChange={() =>
                        setOrgSettingsModal((m) => ({
                          ...m,
                          complianceAssessmentEnabled: option.value,
                        }))
                      }
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>

              <div className="orgSettingsSectionTitle" id="orgPepAiProviderLabel">PEP public-web search · AI provider</div>
              <div className="orgSettingsOptions orgSettingsOptions--compact" role="radiogroup" aria-labelledby="orgPepAiProviderLabel" aria-describedby="orgPepAiProviderNote">
                {PEP_AI_PROVIDERS.map(option => (
                  <label key={option.value} className={`orgSettingsOption${orgSettingsModal.pepAiProvider === option.value ? " is-selected" : ""}`}>
                    <input type="radio" name="pepAiProvider" value={option.value}
                      checked={orgSettingsModal.pepAiProvider === option.value}
                      disabled={savingOrgSettings}
                      onChange={() => setOrgSettingsModal(previous => ({ ...previous, pepAiProvider: option.value }))} />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              <p className="small text-muted" id="orgPepAiProviderNote">
                {pepAiProviderDescription(orgSettingsModal.pepAiProvider)}
              </p>

              <div className="orgSettingsSectionTitle">Adverse media screening</div>
              <div className="row g-3 mb-3">
                {[
                  { key: 'resultsPerQuery', field: 'adverseResultsPerQuery', label: 'Results per search query' },
                  { key: 'articlesPerSubject', field: 'adverseArticlesPerSubject', label: 'Maximum articles assessed per subject' },
                ].map(({ key, field, label }) => (
                  <div className="col-sm-6" key={field}>
                    <label className="form-label small" htmlFor={field}>{label}</label>
                    <input id={field} name={field} type="number" className="form-control"
                      min={ADVERSE_LIMIT_BOUNDS[key][0]} max={ADVERSE_LIMIT_BOUNDS[key][1]} step="1"
                      value={orgSettingsModal[field]} disabled={savingOrgSettings}
                      onChange={event => {
                        const value = event.target.value;
                        setOrgSettingsModal(previous => ({ ...previous, [field]: value }));
                      }} />
                    <div className="small text-muted">{ADVERSE_LIMIT_BOUNDS[key][0]}–{ADVERSE_LIMIT_BOUNDS[key][1]}</div>
                  </div>
                ))}
              </div>
              <p className="small text-muted">Applies to new screenings. More articles may increase screening time and cost. Duplicate URLs are counted once per subject.</p>
              <div className="orgSettingsOptions orgSettingsOptions--compact">
                <label className={`orgSettingsOption${orgSettingsModal.capAdverseAllegations ? " is-selected" : ""}`}>
                  <input type="checkbox" name="capAdverseAllegations"
                    checked={orgSettingsModal.capAdverseAllegations === true}
                    disabled={savingOrgSettings}
                    aria-describedby="orgAdverseAllegationCapNote"
                    onChange={event => setOrgSettingsModal(previous => ({ ...previous, capAdverseAllegations: event.target.checked }))} />
                  <span>Limit allegation-only findings to Medium concern</span>
                </label>
              </div>
              <p className="small text-muted" id="orgAdverseAllegationCapNote">
                Caps High concern at Medium for findings categorised as Allegation. Investigations and other stages remain unchanged.
                Applies to new screenings, independently of Enhanced adverse screening.
              </p>
              <div className="orgSettingsOptions orgSettingsOptions--compact">
                <label className={`orgSettingsOption${orgSettingsModal.enhancedAdverseScreening ? " is-selected" : ""}`}>
                  <input type="checkbox" name="enhancedAdverseScreening"
                    checked={orgSettingsModal.enhancedAdverseScreening === true}
                    disabled={savingOrgSettings}
                    aria-describedby="orgEnhancedAdverseNote"
                    onChange={event => setOrgSettingsModal(previous => ({ ...previous, enhancedAdverseScreening: event.target.checked }))} />
                  <span>Enhanced adverse screening</span>
                </label>
              </div>
              <p className="small text-muted" id="orgEnhancedAdverseNote">
                Check official favourable findings before confirming high concern for articles directly implicating the subject.
                Applies to new screenings and may increase screening time.
              </p>

              <div className="orgSettingsSectionTitle">Regulatory add-ons</div>
              <div className="orgSettingsOptions orgSettingsOptions--compact">
                {REGULATORY_ADD_ONS.map(({ key, label }) => (
                  <label key={key} className={`orgSettingsOption${orgSettingsModal.regulatoryAddOns?.[key] ? " is-selected" : ""}`}>
                    <input type="checkbox" name={`regulatoryAddOn_${key}`}
                      checked={orgSettingsModal.regulatoryAddOns?.[key] === true}
                      disabled={savingOrgSettings}
                      onChange={event => setOrgSettingsModal(previous => ({
                        ...previous,
                        regulatoryAddOns: { ...previous.regulatoryAddOns, [key]: event.target.checked },
                      }))} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <p className="small text-muted">
                Enable each add-on on the Regulatory page for this organisation. Unselected add-ons are disabled and shown as coming soon.
              </p>

              <div className="orgSettingsSectionTitle">Sanctions screening sources</div>
              <div className="small text-muted">
                {sanctionsSourcesForJurisdiction(orgSettingsModal.sanctionsJurisdiction).map(source => source.toUpperCase()).join(" · ") || "Configure the organisation jurisdiction"}
                <div>Determined by the organisation jurisdiction</div>
              </div>

              <div className="orgSettingsSectionTitle">Scann & Check • Document Data Consistency</div>
              <div className="orgSettingsOptions orgSettingsOptions--compact">
                {[
                  { value: true, label: "ID check: Yes" },
                  { value: false, label: "ID check: No" },
                ].map((option) => (
                  <label
                    key={String(option.value)}
                    className={`orgSettingsOption${
                      orgSettingsModal.documentDataConsistencyScanIdEnabled === option.value
                        ? " is-selected"
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="documentDataConsistencyScanIdEnabled"
                      checked={
                        orgSettingsModal.documentDataConsistencyScanIdEnabled ===
                        option.value
                      }
                      onChange={() =>
                        setOrgSettingsModal((m) => ({
                          ...m,
                          documentDataConsistencyScanIdEnabled: option.value,
                        }))
                      }
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>

              <div className="orgSettingsOptions orgSettingsOptions--compact">
                {[
                  { value: true, label: "Proof of address: Yes" },
                  { value: false, label: "Proof of address: No" },
                ].map((option) => (
                  <label
                    key={`poa-${String(option.value)}`}
                    className={`orgSettingsOption${
                      orgSettingsModal.proofOfAddressVerificationEnabled === option.value
                        ? " is-selected"
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="proofOfAddressVerificationEnabled"
                      checked={
                        orgSettingsModal.proofOfAddressVerificationEnabled ===
                        option.value
                      }
                      onChange={() =>
                        setOrgSettingsModal((m) => ({
                          ...m,
                          proofOfAddressVerificationEnabled: option.value,
                        }))
                      }
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>

              {orgSettingsModal.proofOfAddressVerificationEnabled === true && (
                <div className="proofAddressSettingsCard">
                  <div className="proofAddressSettingItem">
                    <span className="proofAddressSettingLabel">Validity</span>
                    <div className="proofAddressValidityControls">
                      <div className="proofAddressSegmented">
                        {[
                          { value: false, label: "Months" },
                          { value: true, label: "Unlimited" },
                        ].map((option) => (
                          <button
                            key={String(option.value)}
                            type="button"
                            className={
                              orgSettingsModal.proofOfAddressValidityUnlimited ===
                              option.value
                                ? "is-selected"
                                : ""
                            }
                            onClick={() =>
                              setOrgSettingsModal((m) => ({
                                ...m,
                                proofOfAddressValidityUnlimited: option.value,
                              }))
                            }
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                      {orgSettingsModal.proofOfAddressValidityUnlimited !== true ? (
                        <div className="proofAddressNumberControl">
                          <input
                            type="number"
                            min="1"
                            max={PROOF_OF_ADDRESS_MAX_AGE_MONTHS}
                            value={orgSettingsModal.proofOfAddressMaxAgeMonths}
                            onChange={(e) =>
                              setOrgSettingsModal((m) => ({
                                ...m,
                                proofOfAddressMaxAgeMonths: e.target.value,
                              }))
                            }
                          />
                          <span>months</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="proofAddressSettingItem">
                    <span className="proofAddressSettingLabel">Issuer DB</span>
                    <div className="proofAddressSegmented">
                      {[
                        { value: true, label: "On" },
                        { value: false, label: "Off" },
                      ].map((option) => (
                        <button
                          key={String(option.value)}
                          type="button"
                          className={
                            orgSettingsModal.proofOfAddressIssuerCheckEnabled ===
                            option.value
                              ? "is-selected"
                              : ""
                          }
                          onClick={() =>
                            setOrgSettingsModal((m) => ({
                              ...m,
                              proofOfAddressIssuerCheckEnabled: option.value,
                            }))
                          }
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="orgSettingsOptions orgSettingsOptions--compact">
                {[
                  { value: true, label: "Corporate document: Yes" },
                  { value: false, label: "Corporate document: No" },
                ].map((option) => (
                  <label
                    key={`corporate-${String(option.value)}`}
                    className={`orgSettingsOption${
                      orgSettingsModal.corporateDocumentVerificationEnabled === option.value
                        ? " is-selected"
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="corporateDocumentVerificationEnabled"
                      checked={
                        orgSettingsModal.corporateDocumentVerificationEnabled ===
                        option.value
                      }
                      onChange={() =>
                        setOrgSettingsModal((m) => ({
                          ...m,
                          corporateDocumentVerificationEnabled: option.value,
                        }))
                      }
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>

              {orgSettingsModal.corporateDocumentVerificationEnabled === true && (
                <div className="proofAddressSettingsCard">
                  <div className="proofAddressSettingItem">
                    <span className="proofAddressSettingLabel">Validity</span>
                    <div className="proofAddressValidityControls">
                      <div className="proofAddressSegmented">
                        {[
                          { value: false, label: "Months" },
                          { value: true, label: "Unlimited" },
                        ].map((option) => (
                          <button
                            key={`corporate-validity-${String(option.value)}`}
                            type="button"
                            className={
                              orgSettingsModal.corporateDocumentValidityUnlimited ===
                              option.value
                                ? "is-selected"
                                : ""
                            }
                            onClick={() =>
                              setOrgSettingsModal((m) => ({
                                ...m,
                                corporateDocumentValidityUnlimited: option.value,
                              }))
                            }
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                      {orgSettingsModal.corporateDocumentValidityUnlimited !== true ? (
                        <div className="proofAddressNumberControl">
                          <input
                            type="number"
                            min="1"
                            max={PROOF_OF_ADDRESS_MAX_AGE_MONTHS}
                            value={orgSettingsModal.corporateDocumentMaxAgeMonths}
                            onChange={(e) =>
                              setOrgSettingsModal((m) => ({
                                ...m,
                                corporateDocumentMaxAgeMonths: e.target.value,
                              }))
                            }
                          />
                          <span>months</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="createOrgModalActions">
              <button
                className="deletionProgressCloseBtn"
                style={{ background: "#90a7bb" }}
                onClick={handleSaveOrgSettings}
                disabled={savingOrgSettings}
              >
                {savingOrgSettings ? "Saving..." : "Save"}
              </button>
              <button
                className="deletionProgressCloseBtn"
                style={{ background: "#b3b6bd" }}
                onClick={() => setOrgSettingsModal(null)}
                disabled={savingOrgSettings}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Edit staff/user modal */}
      {editUserModal && (
        <div
          className="deletionProgressBackdrop"
          onClick={() => !savingUserEdit && !savingReassign && closeEditUserModal()}
        >
          <div
            className={`createOrgModal editUserModal ${
              reassignModal?.user?.uid === editUserModal.uid ? "reassignTeamModal" : ""
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="deletionProgressIcon">
              <i className="bi bi-person-gear" />
            </div>

            <span className="deletionProgressTitle">Edit staff member</span>
            <p className="editUserModalEmail">{editUserModal.email}</p>

            <div className="w-100">
              <div className="mb-3">
                <label className="form-label small text-muted mb-1">
                  Full name
                </label>
                <input
                  className="form-control"
                  value={editUserModal.fullName}
                  onChange={(e) =>
                    setEditUserModal((p) => ({ ...p, fullName: e.target.value }))
                  }
                  autoFocus
                />
              </div>

              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label small text-muted mb-1">
                    Role
                  </label>
                  <Select
                    value={
                      ROLE_OPTIONS.find((o) => o.value === editUserModal.role) ||
                      ROLE_OPTIONS[0]
                    }
                    options={ROLE_OPTIONS}
                    isSearchable={false}
                    styles={selectStyles}
                    onChange={(opt) => {
                      const nextRole = opt?.value || ROLES.STAFF_FRONT;
                      const staffLike = isStaffCategory(nextRole);
                      setEditUserModal((p) => ({
                        ...p,
                        role: nextRole,
                        teamMode: staffLike ? p.teamMode || "none" : "none",
                        teamId: staffLike ? p.teamId : "",
                        teamName: staffLike ? p.teamName : "",
                      }));
                    }}
                  />
                </div>

                <div className="col-6">
                  <label className="form-label small text-muted mb-1">
                    Status
                  </label>
                  <Select
                    value={
                      USER_STATUS_OPTIONS.find((o) =>
                        editUserModal.disabled
                          ? o.value === "disabled"
                          : o.value === "active"
                      ) || USER_STATUS_OPTIONS[0]
                    }
                    options={USER_STATUS_OPTIONS}
                    isSearchable={false}
                    styles={selectStyles}
                    onChange={(opt) =>
                      setEditUserModal((p) => ({
                        ...p,
                        disabled: opt?.value === "disabled",
                      }))
                    }
                  />
                </div>
              </div>

              {reassignModal?.user?.uid === editUserModal.uid && renderReassignContent()}

              <div className="createOrgModalActions">
                <button
                  type="button"
                  className="deletionProgressCloseBtn"
                  style={{ background: "#90a7bb" }}
                  onClick={handleSaveUserEdit}
                  disabled={savingUserEdit || savingReassign}
                >
                  {savingUserEdit || savingReassign ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  className="deletionProgressCloseBtn"
                  style={{ background: "#b3b6bd" }}
                  onClick={closeEditUserModal}
                  disabled={savingUserEdit || savingReassign}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Client reassignment before staff deletion */}
      {deleteClientReassignModal && (
        <div
          className="deletionProgressBackdrop"
          onClick={() => !resolvingDeleteClients && setDeleteClientReassignModal(null)}
        >
          <div
            className="createOrgModal editUserModal"
            style={{ maxWidth: 560 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="deletionProgressIcon">
              <i className="bi bi-arrow-left-right" />
            </div>

            <span className="deletionProgressTitle">Reassign clients</span>
            <p className="editUserModalEmail">
              {deleteClientReassignModal.user?.fullName ||
                deleteClientReassignModal.user?.email}
            </p>

            <div className="reassignImpactBox w-100">
              <div>Own clients: {deleteClientReassignModal.impact?.ownClientCount || 0}</div>
              <div>
                Current team clients:{" "}
                {deleteClientReassignModal.impact?.oldTeamClientCount || 0}
              </div>
              <div>
                Remaining staff in current team:{" "}
                {deleteClientReassignModal.impact?.remainingOldTeamStaff || 0}
              </div>
              <div className="reassignRiskText">
                Orphan risk: {deleteClientReassignModal.affectedClients?.length || 0} client(s)
              </div>
            </div>

            <div className="w-100 mt-3">
              <label className="form-label small text-muted mb-1">
                Target assignment
              </label>
              <Select
                value={
                  [
                    {
                      value: "staff",
                      label: "Assign to an existing staff member",
                      isDisabled: deleteClientStaffOptions.length === 0,
                    },
                    {
                      value: "team",
                      label: "Assign to an existing team",
                      isDisabled: deleteClientTeamOptions.length === 0,
                    },
                    {
                      value: "delete",
                      label: "Delete orphan(s)",
                    },
                  ].find((o) => o.value === deleteClientTargetMode) || null
                }
                options={[
                  {
                    value: "staff",
                    label: "Assign to an existing staff member",
                    isDisabled: deleteClientStaffOptions.length === 0,
                  },
                  {
                    value: "team",
                    label: "Assign to an existing team",
                    isDisabled: deleteClientTeamOptions.length === 0,
                  },
                  {
                    value: "delete",
                    label: "Delete orphan(s)",
                  },
                ]}
                isSearchable={false}
                styles={selectStyles}
                onChange={(opt) => {
                  const nextMode = opt?.value || "staff";
                  setDeleteClientTargetMode(nextMode);
                  setDeleteClientTargetStaff(null);
                  setDeleteClientTargetTeam(null);
                }}
              />
            </div>

            {deleteClientTargetMode === "staff" && (
              <div className="w-100 mt-3">
                <label className="form-label small text-muted mb-1">
                  Staff member
                </label>
                <Select
                  value={deleteClientTargetStaff}
                  options={deleteClientStaffOptions}
                  isSearchable
                  isClearable
                  placeholder="Search staff..."
                  styles={selectStyles}
                  onChange={setDeleteClientTargetStaff}
                  formatOptionLabel={(opt) => (
                    <div className="d-flex flex-column">
                      <span>{opt.fullName || opt.email || opt.label}</span>
                      {opt.email && (
                        <span style={{ fontSize: 11, color: "#8a96a3" }}>
                          {opt.email}
                        </span>
                      )}
                    </div>
                  )}
                />
              </div>
            )}

            {deleteClientTargetMode === "team" && (
              <div className="w-100 mt-3">
                <label className="form-label small text-muted mb-1">
                  Team
                </label>
                <Select
                  value={deleteClientTargetTeam}
                  options={deleteClientTeamOptions}
                  isSearchable
                  isClearable
                  placeholder="Search team..."
                  styles={selectStyles}
                  onChange={setDeleteClientTargetTeam}
                  formatOptionLabel={(opt) => (
                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <span>{opt.teamName || opt.label}</span>
                      <span style={{ fontSize: 11, color: "#8a96a3" }}>
                        {opt.staffCount} staff
                      </span>
                    </div>
                  )}
                />
              </div>
            )}

            <div className="createOrgModalActions">
              <button
                type="button"
                className="deletionProgressCloseBtn"
                style={{
                  background: deleteClientTargetMode === "delete" ? "#df8387" : "#90a7bb",
                }}
                onClick={() =>
                  deleteClientTargetMode === "delete"
                    ? confirmDeleteOrphanClients()
                    : resolveDeleteClientReassignment("reassign")
                }
                disabled={
                  resolvingDeleteClients ||
                  (deleteClientTargetMode === "staff" && !deleteClientTargetStaff?.value) ||
                  (deleteClientTargetMode === "team" && !deleteClientTargetTeam?.value)
                }
              >
                {resolvingDeleteClients
                  ? "Working..."
                  : deleteClientTargetMode === "delete"
                  ? "Delete orphan(s)"
                  : "Reassign clients"}
              </button>
              <button
                type="button"
                className="deletionProgressCloseBtn"
                style={{ background: "#b3b6bd" }}
                onClick={() => setDeleteClientReassignModal(null)}
                disabled={resolvingDeleteClients}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Reassign team modal */}
      {reassignModal && !editUserModal && (
        <div
          className="deletionProgressBackdrop"
          onClick={() => !savingReassign && setReassignModal(null)}
        >
          <div
            className="createOrgModal editUserModal reassignTeamModal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="deletionProgressIcon">
              <i className="bi bi-diagram-3" />
            </div>

            <span className="deletionProgressTitle">Reassign team</span>
            <p className="editUserModalEmail">
              {reassignModal.user?.fullName || reassignModal.user?.email}
            </p>

            {(() => {
              const impact = getReassignImpact();
              const selectedSet = new Set(reassignSelectedClientIds);
              const ownClientsDisabled =
                impact.ownClients.length === 0 || !ownClientsCoverOrphanRisk(impact);
              const targetOptions = [
                { value: "none", label: "No team" },
                { value: "new", label: "New team" },
                {
                  value: "existing",
                  label: "Existing team",
                  isDisabled: reassignTeamOptions.length === 0,
                },
              ];
              const staffOnlyDisabled = impact.orphanRiskCount > 0;

              return (
                <div className="w-100">
                  <div className="mb-3">
                    <label className="form-label small text-muted mb-1">
                      New access
                    </label>
                    <Select
                      value={
                        targetOptions.find((o) => o.value === reassignTeamMode) ||
                        targetOptions[0]
                      }
                      options={targetOptions}
                      isSearchable={false}
                      styles={selectStyles}
                      onChange={(opt) => {
                        const mode = opt?.value || "none";
                        setReassignTeamMode(mode);
                        setReassignTeamName("");
                        setReassignTeamOption(null);
                      }}
                    />
                  </div>

                  {reassignTeamMode === "new" && (
                    <div className="mb-3">
                      <label className="form-label small text-muted mb-1">
                        Team name
                      </label>
                      <input
                        className="form-control"
                        value={reassignTeamName}
                        onChange={(e) => setReassignTeamName(e.target.value)}
                        placeholder={`${reassignModal.user?.fullName || "Staff"}'s team`}
                      />
                    </div>
                  )}

                  {reassignTeamMode === "existing" && (
                    <div className="mb-3">
                      <label className="form-label small text-muted mb-1">
                        Existing team
                      </label>
                      <Select
                        value={reassignTeamOption}
                        options={reassignTeamOptions}
                        isSearchable
                        isClearable
                        placeholder="Search team..."
                        styles={selectStyles}
                        onChange={setReassignTeamOption}
                        formatOptionLabel={(opt) => (
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <span>{opt.teamName || opt.label}</span>
                            <span style={{ fontSize: 11, color: "#8a96a3" }}>
                              {opt.staffCount} staff
                            </span>
                          </div>
                        )}
                      />
                    </div>
                  )}

                  <div className="reassignImpactBox">
                    <div>Own clients: {impact.ownClients.length}</div>
                    <div>
                      Current team clients: {impact.oldTeamClients.length}
                    </div>
                    <div>
                      Remaining staff in current team: {impact.remainingOldTeamStaff}
                    </div>
                    {impact.orphanRiskCount > 0 && (
                      <div className="reassignRiskText">
                        Orphan risk: {impact.orphanRiskCount} client(s)
                      </div>
                    )}
                  </div>

                  <div className="reassignMoveOptions">
                    <label className={staffOnlyDisabled ? "is-disabled" : ""}>
                      <input
                        type="radio"
                        name="reassignMoveMode"
                        checked={reassignMoveMode === "staff_only"}
                        disabled={staffOnlyDisabled}
                        onChange={() => setReassignMoveMode("staff_only")}
                      />
                      <span>Move staff only</span>
                    </label>

                    <label className={ownClientsDisabled ? "is-disabled" : ""}>
                      <input
                        type="radio"
                        name="reassignMoveMode"
                        checked={reassignMoveMode === "own_clients"}
                        disabled={ownClientsDisabled}
                        onChange={() => setReassignMoveMode("own_clients")}
                      />
                      <span>
                        Move staff + staff&apos;s own clients ({impact.ownClients.length} clients)
                      </span>
                    </label>

                    {impact.orphanRiskCount > 0 && (
                      <label>
                        <input
                          type="radio"
                          name="reassignMoveMode"
                          checked={reassignMoveMode === "affected_clients"}
                          onChange={() => setReassignMoveMode("affected_clients")}
                        />
                        <span>
                          Move staff + orphan-risk clients ({impact.orphanRiskCount} clients)
                        </span>
                      </label>
                    )}

                    <label className={impact.allMovable.length === 0 ? "is-disabled" : ""}>
                      <input
                        type="radio"
                        name="reassignMoveMode"
                        checked={reassignMoveMode === "selected_clients"}
                        disabled={impact.allMovable.length === 0}
                        onChange={() => setReassignMoveMode("selected_clients")}
                      />
                      <span>Choose clients to move</span>
                    </label>
                  </div>

                  {impact.orphanRiskCount > 0 && (
                    <div className="reassignDisabledHint">
                      Disabled options would leave {impact.orphanRiskCount} client(s) without staff/team access.
                    </div>
                  )}

                  {reassignMoveMode === "selected_clients" && (
                    <div className="reassignClientList">
                      {impact.allMovable.map((client) => (
                        <label key={client.id} className="reassignClientRow">
                          <input
                            type="checkbox"
                            checked={selectedSet.has(client.id)}
                            onChange={(e) => {
                              setReassignSelectedClientIds((prev) =>
                                e.target.checked
                                  ? [...new Set([...prev, client.id])]
                                  : prev.filter((id) => id !== client.id)
                              );
                            }}
                          />
                          <span>{client.clientName || client.name || client.id}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  <div className="createOrgModalActions">
                    <button
                      type="button"
                      className="deletionProgressCloseBtn"
                      style={{ background: "#90a7bb" }}
                      onClick={handleSaveReassign}
                      disabled={savingReassign}
                    >
                      {savingReassign ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      className="deletionProgressCloseBtn"
                      style={{ background: "#b3b6bd" }}
                      onClick={() => setReassignModal(null)}
                      disabled={savingReassign}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* ✅ Team access modal */}
      {teamAccessModal && (
        <div className="deletionProgressBackdrop" onClick={() => setTeamAccessModal(null)}>
          <div className="createOrgModal" onClick={(e) => e.stopPropagation()}>
            <div className="deletionProgressIcon">
              <i className="bi bi-diagram-3" />
            </div>

            <span className="deletionProgressTitle">
              {teamAccessModal === "new" ? "New team" : "Existing team"}
            </span>

            <div className="w-100">
              {teamAccessModal === "new" ? (
                <div className="mb-3">
                  <label className="form-label small text-muted mb-1">
                    Team name
                  </label>
                  <input
                    className="form-control"
                    value={pendingTeamName}
                    onChange={(e) => setPendingTeamName(e.target.value)}
                    placeholder={fullName.trim() ? `${fullName.trim()}'s team` : "Team name"}
                    autoFocus
                  />
                </div>
              ) : (
                <div className="mb-3">
                  <label className="form-label small text-muted mb-1">
                    Join team
                  </label>
                  <Select
                    value={pendingTeamOption}
                    options={teamOptions}
                    isSearchable
                    isClearable
                    placeholder="Search team..."
                    styles={selectStyles}
                    onChange={setPendingTeamOption}
                    formatOptionLabel={(opt) => (
                      <div className="d-flex align-items-center justify-content-between gap-2">
                        <span>{opt.teamName || opt.label}</span>
                        <span style={{ fontSize: 11, color: "#8a96a3" }}>
                          {opt.staffCount} staff
                        </span>
                      </div>
                    )}
                  />
                </div>
              )}

              <div className="createOrgModalActions">
                <button
                  type="button"
                  className="deletionProgressCloseBtn"
                  style={{ background: "#90a7bb" }}
                  onClick={applyTeamAccessModal}
                >
                  Apply
                </button>
                <button
                  type="button"
                  className="deletionProgressCloseBtn"
                  style={{ background: "#b3b6bd" }}
                  onClick={() => setTeamAccessModal(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Seed demo clients into a team or a no-team staff member's own scope */}
      {seedTeamModal && (
        <div
          className="deletionProgressBackdrop"
          onClick={() => {
            if (seeding) return;
            setSeedTeamModal(null);
            setSelectedSeedTeamOption(null);
          }}
        >
          <div className="createOrgModal" onClick={(e) => e.stopPropagation()}>
            <div className="deletionProgressIcon">
              <i className="bi bi-people" />
            </div>

            <span className="deletionProgressTitle">Seed demo clients</span>

            <div className="w-100">
              <p className="deletionProgressText mb-3">
                Choose the team or no-team staff member that should receive the 50 demo clients for{" "}
                <strong>{seedTeamModal.orgName || seedTeamModal.orgId}</strong>.
              </p>

              <div className="mb-3 seedTeamSelectWrap">
                <label className="form-label small text-muted mb-1">
                  Target
                </label>
                <Select
                  value={selectedSeedTeamOption}
                  options={selectedSeedTeamOptions}
                  isSearchable
                  isClearable
                  placeholder="Search team or staff..."
                  styles={selectStyles}
                  onChange={setSelectedSeedTeamOption}
                  formatOptionLabel={(opt) => (
                    <div className="d-flex align-items-center justify-content-between gap-2">
                      <span>{opt.label}</span>
                      <span style={{ fontSize: 11, color: "#8a96a3" }}>
                        {opt.targetType === "team"
                          ? `${opt.staffCount} staff`
                          : "no team"}
                      </span>
                    </div>
                  )}
                />
              </div>

              {selectedSeedTeamOptions.length === 0 && (
                <p className="text-danger small mb-2">
                  This organisation has no staff member yet.
                </p>
              )}

              <div className="createOrgModalActions">
                <button
                  type="button"
                  className="deletionProgressCloseBtn"
                  style={{ background: "#90a7bb" }}
                  disabled={seeding || !selectedSeedTeamOption}
                  onClick={runSeedClientsForTeam}
                >
                  {seeding ? "Seeding..." : "Seed"}
                </button>
                <button
                  type="button"
                  className="deletionProgressCloseBtn"
                  style={{ background: "#b3b6bd" }}
                  disabled={seeding}
                  onClick={() => {
                    setSeedTeamModal(null);
                    setSelectedSeedTeamOption(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ Empty-users deletion progress modal */}
      {deletionProgress && (
        <div className="deletionProgressBackdrop">
          <div className="deletionProgressCard">
            <div className="deletionProgressIcon">
              {deletionProgress.finished && !deletionProgress.error ? (
                <i className="bi bi-check-lg" />
              ) : deletionProgress.error ? (
                <i className="bi bi-exclamation-triangle" />
              ) : (
                <i className="bi bi-person-x-fill" />
              )}
            </div>

            <span className="deletionProgressTitle">
              {deletionProgress.error
                ? "Error"
                : deletionProgress.finished
                ? "Done"
                : "Deleting empty users…"}
            </span>

            {deletionProgress.error ? (
              <p className="deletionProgressText">{deletionProgress.error}</p>
            ) : (
              <>
                <p className="deletionProgressText">
                  {deletionProgress.done} / {deletionProgress.total} user(s) deleted
                </p>
                <div className="deletionProgressBarWrap">
                  <div
                    className="deletionProgressBar"
                    style={{
                      width: `${Math.round((deletionProgress.done / deletionProgress.total) * 100)}%`,
                    }}
                  />
                </div>
              </>
            )}

            {deletionProgress.finished && (
              <button
                className="deletionProgressCloseBtn"
                onClick={() => setDeletionProgress(null)}
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}

      {/* -------- TODO MODAL -------- */}
      {showTodoModal && (
        <div
          className="deletionProgressBackdrop"
          onClick={() => setShowTodoModal(false)}
        >
          <div
            className="adminTodoModal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="adminTodoHeader">
              <div className="adminTodoTitle">
                <i className="bi bi-check2-square me-2" aria-hidden="true" />
                TODO List
              </div>
              <button
                type="button"
                className="adminTodoClose"
                onClick={() => setShowTodoModal(false)}
                aria-label="Close"
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>

            {todoLoading ? (
              <div className="adminTodoLoading">Loading…</div>
            ) : (
              <div className="adminTodoBody">
                {TODO_CATEGORIES.map((cat) => {
                  const catItems = todos.filter((t) => t.category === cat.key);
                  const pending = catItems
                    .filter((t) => !t.completed)
                    .sort((a, b) => {
                      const aPrio = a.priority === "high" ? 1 : 0;
                      const bPrio = b.priority === "high" ? 1 : 0;
                      if (bPrio !== aPrio) return bPrio - aPrio;
                      const aTop = a.topOrder || 0;
                      const bTop = b.topOrder || 0;
                      if (bTop !== aTop) return bTop - aTop;
                      const aCreated = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
                      const bCreated = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
                      return aCreated - bCreated;
                    });
                  const done = catItems.filter((t) => t.completed);
                  return (
                    <div key={cat.key} className="adminTodoSection">
                      <div className="adminTodoSectionTitle">{cat.label}</div>

                      <form
                        className="adminTodoAddRow"
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleAddTodo(cat.key);
                        }}
                      >
                        <input
                          className="adminTodoInput"
                          placeholder={`Add ${cat.label.toLowerCase()}…`}
                          value={todoInputs[cat.key]}
                          onChange={(e) =>
                            setTodoInputs((prev) => ({
                              ...prev,
                              [cat.key]: e.target.value,
                            }))
                          }
                        />
                        <div className="adminTodoPriorityToggle">
                          {["normal", "high"].map((p) => (
                            <button
                              key={p}
                              type="button"
                              className={`adminTodoPriorityBtn${todoPriority[cat.key] === p ? " is-active" : ""}${p === "high" ? " is-high" : ""}`}
                              onClick={() =>
                                setTodoPriority((prev) => ({ ...prev, [cat.key]: p }))
                              }
                            >
                              {p === "high" ? "High" : "Normal"}
                            </button>
                          ))}
                        </div>
                        <button
                          type="submit"
                          className="adminTodoAddBtn"
                          disabled={!todoInputs[cat.key].trim() || todoSaving[cat.key]}
                        >
                          Add
                        </button>
                      </form>

                      {pending.map((todo) => (
                        <div key={todo.id} className={`adminTodoItem${todo.priority === "high" ? " adminTodoItem--high" : ""}`}>
                          <button
                            type="button"
                            className="adminTodoCheck"
                            onClick={() => handleToggleTodo(todo)}
                            title="Mark as done"
                          >
                            <i className="bi bi-circle" aria-hidden="true" />
                          </button>
                          <span className="adminTodoText">{todo.text}</span>
                          <button
                            type="button"
                            className="adminTodoMoveTop"
                            onClick={() => handleMoveToTop(todo)}
                            title="Move to top"
                          >
                            <i className="bi bi-arrow-up" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="adminTodoDelete"
                            onClick={() => handleDeleteTodo(todo.id)}
                            title="Delete"
                          >
                            <i className="bi bi-trash3" aria-hidden="true" />
                          </button>
                        </div>
                      ))}

                      {done.map((todo) => (
                        <div key={todo.id} className="adminTodoItem adminTodoItem--done">
                          <button
                            type="button"
                            className="adminTodoCheck"
                            onClick={() => handleToggleTodo(todo)}
                            title="Mark as pending"
                          >
                            <i className="bi bi-check-circle-fill" aria-hidden="true" />
                          </button>
                          <span className="adminTodoText">{todo.text}</span>
                          <button
                            type="button"
                            className="adminTodoDelete"
                            onClick={() => handleDeleteTodo(todo.id)}
                            title="Delete"
                          >
                            <i className="bi bi-trash3" aria-hidden="true" />
                          </button>
                        </div>
                      ))}

                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      <Toast ref={toastRef} />
    </motion.div>
  );
}
